import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  X,
  PencilLine,
  ChevronDown,
  ChevronUp,
  Check,
  Info,
  PaintbrushVertical,
} from "lucide-react";
import { useHistory, useLocation } from "react-router-dom";
import noBrandImage from "../../assets/img/brand/no-brand-img.png";
import emptySearchImg from "../../assets/img/brand/empty-search.png";
import Pagination from "../../components/Pagination";
import Spinner from "../../components/Spinner";
import SearchBox from "../../components/SearchBox";
import ListingSearchBars from "../../components/ListingSearchBars";
import FilterDrawer from "../../components/FilterDrawer";
import AlertNotification from "../../components/AlertNotification";
import LoadingNotification from "../../components/LoadingNotification";
import apiHelper from "../../apiHelper";
import noImage from "../../assets/img/brand/noImage.png";
import { useLoader } from "../../components/GlobalLoader";
import { formatDateTime } from "../../utils/formateDate";
import { isPlaceholderStock } from "../../utils/placeholderStock";
import { getContentColumnWidth } from "../../utils/columnWidth";
import SortableHeader from "../../components/SortableHeader";
import StockTags from "../../components/StockTags";
import PricingSaveSummary, { hasPricingSaveDetails } from "../../components/PricingSaveSummary";
import {
  isWooDrivenBasePrice,
  wooBasePriceDriver,
  isWooPlatformName,
  isBasePriceEmpty,
  WOO_BASE_PRICE_LOCKED_TITLE,
  BASE_PRICE_BREATHE_TITLE,
} from "../../utils/basePriceSource";

const SET_TYPE_OPTIONS = ["Primary", "Telco", "None"];

const PAGE_SIZE = 20;
const PH_PAGE_SIZE = 10;

function formatSGD(value) {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return "-";
  // Comma-grouped like Platform Listing's formatPrice ("SGD 3,279.00"), so the
  // two pages read the same - and so does the inline "Cannot exceed SGD …"
  // message, which is built from this. Everything that parses a formatted
  // price back strips non-digits first (see the replace(/[^\d.]/g) calls), so
  // the separators never reach a number.
  return `SGD ${num.toLocaleString("en-SG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatPrice(value) {
  if (!value && value !== 0) return "-";
  const m = String(value).replace(/[^\d.-]/g, "");
  if (m === "") return "-";
  const num = Number(m.replace(/,/g, ""));
  if (isNaN(num)) return "-";
  return num.toLocaleString("en-SG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function getPlatformSyncStatus(p) {
  const hasPlatformPrice = p?.platformPrice && p?.platformPrice !== "-";
  const backendStatus = p?.syncstatus;

  //for WooCommerce stays enabled even without a platformPrice to compare against.
  const isWooCommerce = (p?.name || "").includes("WooCommerce");
  if (isWooCommerce && !hasPlatformPrice) {
    return backendStatus === "success" ? "MATCH" : "FAILED";
  }

  // Lazada campaign price lock: platform and ERP prices differ *by design*
  // (Lazada refuses the edit until the campaign ends), and the mapping itself
  // is healthy. Green dot + "Campaign" tag, not a red mismatch.
  if (backendStatus === "locked") return "MATCH";

  // ERP-5170: 'success_delayed' is the state retry_lazada_locked_prices leaves
  // behind when a campaign lifts and the parked price finally goes out. It is a
  // success — the delay is a fact about that one price change and lives in the
  // Pricing History row, not a lasting condition of the mapping.
  if (backendStatus === "success_delayed") return "MATCH";

  // success (and matching price) -> green; every other case (failed, pending,
  // never, price mismatch) -> red.
  if (backendStatus === "success" && hasPlatformPrice) {
    const platformNum = parseFloat(p?.platformPrice.replace(/[^\d.]/g, ""));
    const erpNum = parseFloat((p?.erpPrice || "").replace(/[^\d.]/g, ""));
    if (!isNaN(platformNum) && platformNum === erpNum) return "MATCH";
  }
  return "FAILED";
}

function getPlatformVisual(status) {
  if (status === "MATCH") return { dotColor: "#146C43", textColor: "#212529" };
  return { dotColor: "#B02A37", textColor: "#58151C" };
}

// Amber "Campaign" pill (Figma) next to a Lazada platform whose price is
// locked by a running campaign (sync_status 'locked'). Same shape as the
// Delist pill on Platform Listing. Lazada exposes no campaign name or dates
// through its API, so the tag can only say that a campaign is on.
const CAMPAIGN_TAG_TITLE =
  "Under Lazada campaign — the platform price is frozen. ERP price edits are kept and pushed to Lazada once the campaign ends (retried daily 1:00pm SGT).";
// Delist pill — same look as Platform Listing's, so a delisted mapping reads the
// same wherever it shows up.
const DELIST_TAG_STYLE = {
  display: "flex",
  padding: "2px 5px",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: 99,
  border: "1px solid #B02A37",
  background: "#F8D7DA",
  color: "#B02A37",
  textAlign: "center",
  fontFamily: "Montserrat",
  fontSize: 8,
  fontWeight: 600,
  lineHeight: "normal",
  flexShrink: 0,
};

const CAMPAIGN_TAG_STYLE = {
  display: "inline-flex",
  padding: "2px 5px",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: 99,
  border: "1px solid #CC9A06",
  background: "#FFF3CD",
  color: "#CC9A06",
  fontFamily: "Montserrat",
  fontSize: 8,
  fontWeight: 600,
  lineHeight: "normal",
  flexShrink: 0,
};

const isLazadaPlatform = (name = "") => name.includes("Lazada");

// Lazada-only info icon + tooltip explaining the struck-through Normal Price
// shown next to a Platform Price that is below it.
function NormalPriceInfo() {
  const [tipPos, setTipPos] = useState(null);
  const showTip = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    // Anchor the tooltip's top-right corner at the icon, so the box opens
    // down and to the left of it.
    setTipPos({ right: window.innerWidth - r.right, top: r.bottom + 8 });
  };
  const hideTip = () => setTipPos(null);
  return (
    <span
      className="ppv2-normprice-info"
      onMouseEnter={showTip}
      onMouseLeave={hideTip}
      onClick={(e) => e.stopPropagation()}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 15 15" fill="none">
        <g clipPath="url(#clip0_ppv2_normprice)">
          <path d="M7.5 10V7.5M7.5 5H7.50625M13.75 7.5C13.75 10.9518 10.9518 13.75 7.5 13.75C4.04822 13.75 1.25 10.9518 1.25 7.5C1.25 4.04822 4.04822 1.25 7.5 1.25C10.9518 1.25 13.75 4.04822 13.75 7.5Z" stroke="#666666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </g>
        <defs><clipPath id="clip0_ppv2_normprice"><rect width="15" height="15" fill="white"/></clipPath></defs>
      </svg>
      {tipPos && (
        <div
          className="ppv2-normprice-tip"
          style={{ right: tipPos.right, top: tipPos.top }}
        >
          <div className="ppv2-normprice-tip-title">Normal Price</div>
          <div>The normal price that retrieve from lazada</div>
        </div>
      )}
    </span>
  );
}

// Whether a Lazada row shows its struck-through Normal (list) price beside the
// Special Price: whenever Lazada gave us one, even when the two are equal
// (ERP-5162: the stakeholder wants the Normal Price visible in every case, not
// only when there is a discount).
function shouldShowNormalPrice(p) {
  return isLazadaPlatform(p.name) && p.normalPriceRaw != null && p.override_price_raw != null;
}

// erpPrice (the ERP-set Platform Price) plus, for a Lazada row priced below
// its Normal (list) price, a struck-through Normal Price and an info icon —
// all on one line. Used on the stock-group row's stacked platform list.
// The text a platform row puts in the Platform Price cell. One place, because
// the column is also SIZED from these strings (see the colWidths effect).
function erpPriceParts(p) {
  return {
    priceText: p.erpPrice,
    normalText: shouldShowNormalPrice(p) ? p.normalPrice : null,
  };
}

// Non-text chrome the cell adds around those strings: the two 4px gaps and the
// 12px info icon on the group row, or the 4px gap and edit pencil on a mapping
// row - plus slack for the space that stands in for the gap when measured.
const PLATFORM_PRICE_CELL_EXTRA = 24;

function renderErpPriceValue(p) {
  const { priceText, normalText } = erpPriceParts(p);
  const priceNode = priceText !== "-" ? priceText : <span style={{ color: "#212529" }}>-</span>;
  if (!normalText) return priceNode;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      <span>{priceNode}</span>
      <span style={{ textDecoration: "line-through", color: "#666666" }}>{normalText}</span>
      <NormalPriceInfo />
    </span>
  );
}

function getPlatformColor(name = "") {
  if (name.includes("Shopee")) return "#EE4D2D";
  if (name.includes("Lazada")) return "#1A9BF7";
  if (name.includes("TikTok")) return "#000000";
  return "#6C757D";
}

function mapApiToUi(item) {
  const platforms = (item.platforms || []).map((p) => {
    const obj = {
      id: p.platform_id,
      sales_listing_mapping_id: p.sales_listing_mapping_id,
      name: p.platform_name,
      color: getPlatformColor(p.platform_name),
      platformPrice: formatSGD(p.platform_price),
      // Unformatted, and never blanked the way platformPrice is on a failed
      // sync — the Base Price rule reads the stored figure, same as the backend.
      platformPriceRaw: p.platform_price != null ? Number(p.platform_price) : null,
      erpPrice: formatSGD(p.override_price),
      normalPrice: formatSGD(p.normal_price),
      normalPriceRaw: p.normal_price != null ? Number(p.normal_price) : null,
      syncstatus: p.sync_status,
      // 'locked' = Lazada campaign price lock: the mapping is healthy (green
      // dot) but an ERP override cannot reach Lazada until the campaign ends.
      isCampaign: String(p.sync_status || "").toLowerCase() === "locked",
      lastSync: p.last_sync_time
        ? new Date(p.last_sync_time).toLocaleString("en-SG")
        : "-",
      override_price_raw: p.override_price,
      platformProductName: p.platform_product_name || "",
      platformProductId: p.platform_product_id || "",
      platformSkuName: p.platform_sku_name || "",
      platformSkuId: p.platform_sku_id || "",
      notes: p.notes || "",
      isDelisted: p.is_delisted || false,
    };
    const status = getPlatformSyncStatus(obj);
    const visual = getPlatformVisual(status);
    return {
      ...obj,
      platformPrice: status === "FAILED" ? "-" : obj.platformPrice,
      syncStatus: status,
      dotColor: visual.dotColor,
      textColor: visual.textColor,
    };
  });

  return {
    id: item.sales_listing_id,
    stockId: item.stock_id,
    name: item.spu_name || "-",
    variant: item.sku_name || "-",
    model_number: item.model_number || "",
    avatar: item.sku_image || noImage,
    condition: item.condition || "-",
    grading: item.condition_type || "-",
    setType: item.set_type || "",
    listingCode: item.listing_code || "",
    notes: item.notes || "",
    basePrice: item.base_price,
    // The Woo price owning Base Price, judged server-side over ALL mappings — the
    // `platforms` list is narrowed by the platform filter, this is not. Read by
    // wooBasePriceDriver as the fallback when no Woo mapping is in the list.
    wooBasePrice: item.woo_base_price != null ? Number(item.woo_base_price) : null,
    costPriceData: item.cost_price_data || null,
    platforms,
  };
}

// ERP-5171: the placeholder stock is the catch-all for platform listings with no real
// stock behind them, so it collects far more mappings than a real stock and its parent
// row would stack one line per mapping and run off the screen. Its Platforms and
// Platform Price columns stop after four lines and end in an ellipsis; Expand still
// lists every mapping as a sub-row.
const PLACEHOLDER_PARENT_ROW_LIMIT = 4;

// Stands in for the mappings the parent row stopped short of, on its own line so the
// two capped columns keep lining up with each other.
const MoreMappingsEllipsis = () => (
  <div style={{ fontSize: 12, color: "#6C757D", lineHeight: "normal" }}>…</div>
);

// Group listings by stock. A placeholder-stock listing is never grouped with another
// — many listings share that stock legitimately. group.key identifies the row (expand
// state); group.stockId stays the real stock.
function groupByStock(listings) {
  const map = new Map();
  for (const l of listings) {
    const key = isPlaceholderStock(l.name, l.variant) ? `l${l.id}` : `s${l.stockId}`;
    if (!map.has(key)) {
      map.set(key, {
        key,
        stockId: l.stockId,
        name: l.name,
        variant: l.variant,
        model_number: l.model_number,
        avatar: l.avatar,
        condition: l.condition,
        grading: l.grading,
        setType: l.setType,
        costPriceData: l.costPriceData,
        listings: [],
      });
    }
    map.get(key).listings.push(l);
  }
  return Array.from(map.values());
}

const FilterActiveIcon = () => (
  <svg style={{ margin: 0 }} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20.6325 6.44444C20.7252 6.24267 20.7746 6.02858 20.8107 5.74756C21.0354 4.00089 21.1478 3.12756 21.6356 2.56378C20.1234 2 19.2177 2 17.4063 2H4.59369C2.78227 2 1.87655 2 1.36439 2.56378C0.852232 3.12756 0.964572 4.00089 1.18925 5.74756C1.25487 6.25766 1.36399 6.54725 1.69704 6.94296C2.77293 8.22128 4.74334 10.4951 7.50829 12.5623C7.76095 12.7512 7.92676 13.0599 7.95475 13.4016C8.26623 17.2071 8.55159 19.8068 8.70309 21.0865C8.78256 21.7577 9.52673 22.274 10.14 21.8404C11.1702 21.1118 13.0947 20.2952 13.349 18.938C13.4692 18.2964 13.6451 17.1765 13.8437 15.3333" stroke="#0D6EFD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <g clipPath="url(#clip0_ppv2_a)"><path d="M22.8125 11C22.8125 14.2102 20.2102 16.8125 17 16.8125C13.7898 16.8125 11.1875 14.2102 11.1875 11C11.1875 7.78987 13.7898 5.18753 17 5.18753C20.2102 5.18753 22.8125 7.78987 22.8125 11ZM16.3277 14.0777L20.6402 9.7652C20.7866 9.61877 20.7866 9.38132 20.6402 9.23488L20.1099 8.70456C19.9634 9.5581 19.726 9.5581 19.5795 8.70456L16.0625 12.2215L14.4205 10.5795C14.2741 10.4331 14.0366 10.4331 13.8901 10.5795L13.3598 11.1099C13.2134 11.2563 13.2134 11.4937 13.3598 11.6402L15.7973 14.0777C15.9438 14.2241 16.1812 14.2241 16.3277 14.0777Z" fill="#0D6EFD" /></g>
    <defs><clipPath id="clip0_ppv2_a"><rect width="12" height="12" fill="white" transform="translate(11 5)" /></clipPath></defs>
  </svg>
);

const FilterInactiveIcon = () => (
  <svg style={{ margin: 0 }} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M21.6323 6.44444C21.7251 6.24267 21.7744 6.02858 21.8106 5.74756C22.0353 4.00089 22.1476 3.12756 21.6354 2.56378C21.1233 2 20.2176 2 18.4062 2H5.59366C3.78225 2 2.87655 2 2.36439 2.56378C1.85223 3.12756 1.96457 4.00089 2.18925 5.74756C2.25487 6.25766 2.36399 6.54725 2.69703 6.94296C3.77291 8.22128 5.74331 10.4951 8.50823 12.5623C8.76089 12.7512 8.9267 13.0599 8.95469 13.4016C9.26616 17.2071 9.55152 19.8068 9.70302 21.0865C9.78249 21.7577 10.5267 22.274 11.1399 21.8404C12.1701 21.1118 14.0946 20.2952 14.3489 18.938C14.4691 18.2964 14.645 17.1765 14.8436 15.3333" stroke="#6C757D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M18.1113 7.5556V15.3334M22.0001 11.4445L14.2224 11.4445" stroke="#6C757D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const FilterDisabledIcon = () => (
  <svg style={{ margin: 0 }} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M21.6323 6.44444C21.7251 6.24267 21.7744 6.02858 21.8106 5.74756C22.0353 4.00089 22.1476 3.12756 21.6354 2.56378C21.1233 2 20.2176 2 18.4062 2H5.59366C3.78225 2 2.87655 2 2.36439 2.56378C1.85223 3.12756 1.96457 4.00089 2.18925 5.74756C2.25487 6.25766 2.36399 6.54725 2.69703 6.94296C3.77291 8.22128 5.74331 10.4951 8.50823 12.5623C8.76089 12.7512 8.9267 13.0599 8.95469 13.4016C9.26616 17.2071 9.55152 19.8068 9.70302 21.0865C9.78249 21.7577 10.5267 22.274 11.1399 21.8404C12.1701 21.1118 14.0946 20.2952 14.3489 18.938C14.4691 18.2964 14.645 17.1765 14.8436 15.3333" stroke="#CED4DA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M18.1113 7.5556V15.3334M22.0001 11.4445L14.2224 11.4445" stroke="#CED4DA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const PhSortIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ marginLeft: 4, flexShrink: 0 }}>
    <path d="M3 4.5L6 1.5L9 4.5" stroke="#ADB5BD" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M3 7.5L6 10.5L9 7.5" stroke="#ADB5BD" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const PriceUpArrow = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="9" viewBox="0 0 12 9" fill="none">
    <path d="M6 0.75L9.5 7.875L6 6.375L2.5 7.875L6 0.75Z" fill="#13795B" stroke="#13795B" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const PriceDownArrow = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="9" viewBox="0 0 12 9" fill="none">
    <path d="M6 8.25L2.5 1.125L6 2.625L9.5 1.125L6 8.25Z" fill="#B02A37" stroke="#B02A37" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const PlatformMatchIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
    <path d="M8.33333 0C3.73833 0 0 3.73833 0 8.33333C0 12.9283 3.73833 16.6667 8.33333 16.6667C12.9283 16.6667 16.6667 12.9283 16.6667 8.33333C16.6667 3.73833 12.9283 0 8.33333 0ZM7.22913 11.4492C6.91885 11.7595 6.41588 11.7598 6.10527 11.4498L4.16288 9.51161C3.83725 9.18668 3.83659 8.65933 4.16141 8.33359C4.48643 8.00765 5.01418 8.00705 5.33994 8.33225L6.66583 9.65583L10.4883 5.83333C10.8137 5.50795 11.3413 5.50795 11.6667 5.83333C11.9921 6.15872 11.9921 6.68628 11.6667 7.01167L7.22913 11.4492Z" fill="#146C43"/>
  </svg>
);

const PlatformMismatchIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
    <path d="M8.29417 0C3.72083 0 0 3.73833 0 8.33333C0 12.9283 3.73833 16.6667 8.33333 16.6667C12.9283 16.6667 16.6667 12.9283 16.6667 8.33333C16.6667 3.73833 12.9108 0 8.29417 0Z" fill="#CC9A06"/>
  </svg>
);

// ERP-5170. Pending: an amber clock — the price is saved in ERP and has not
// reached the platform yet (a Lazada campaign price lock, or a push deferred
// past the request's time budget). It is deliberately NOT the red failure icon.
const PlatformPendingIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 20 20" fill="none">
    <circle cx="10" cy="10" r="9" fill="#CC9A06" />
    <path d="M10 5.3V10.1L13 11.8" stroke="#FFFFFF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Success (Delayed): the green tick it earned, with the clock tucked into the
// corner — it did reach the platform, just not when it was made. The row's
// Applied Time says when.
const PlatformDelayedIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 22 22" fill="none">
    <circle cx="9" cy="9" r="9" fill="#146C43" />
    <path d="M5.2 9.3L7.9 12L12.9 6.6" stroke="#FFFFFF" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="16" cy="16" r="6" fill="#FFFFFF" />
    <circle cx="16" cy="16" r="4.8" fill="#CC9A06" />
    <path d="M16 13.5V16.1L17.7 17.1" stroke="#FFFFFF" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PlatformFailedIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M9.96079 1.66602C5.38746 1.66602 1.66663 5.40435 1.66663 9.99935C1.66663 14.5943 5.40496 18.3327 9.99996 18.3327C14.595 18.3327 18.3333 14.5943 18.3333 9.99935C18.3333 5.40435 14.5775 1.66602 9.96079 1.66602ZM10.8333 13.3327C10.8333 13.7929 10.4602 14.166 9.99996 14.166C9.53972 14.166 9.16663 13.7929 9.16663 13.3327C9.16663 12.8724 9.53972 12.4993 9.99996 12.4993C10.4602 12.4993 10.8333 12.8724 10.8333 13.3327ZM10.8333 9.99935C10.8333 10.4596 10.4602 10.8327 9.99996 10.8327C9.53972 10.8327 9.16663 10.4596 9.16663 9.99935V6.66602C9.16663 6.20578 9.53972 5.83268 9.99996 5.83268C10.4602 5.83268 10.8333 6.20578 10.8333 6.66602V9.99935Z" fill="#B02A37"/>
  </svg>
);

function getPriceChangeInfo(beforeStr, afterStr) {
  // "BLANK" = explicitly cleared/unset; "-" = no change / not applicable
  if (!afterStr || afterStr === "-") return null;
  if (afterStr === "BLANK") {
    if (!beforeStr || beforeStr === "-" || beforeStr === "BLANK") return null;
    return { direction: "down", amount: null, isCleared: true };
  }
  const after = parseFloat(String(afterStr).replace(/[^\d.]/g, ""));
  if (isNaN(after)) return null;
  if (!beforeStr || beforeStr === "-" || beforeStr === "BLANK") {
    return { direction: "up", amount: after, isFirstTime: true };
  }
  const before = parseFloat(String(beforeStr).replace(/[^\d.]/g, ""));
  if (isNaN(before)) return null;
  const diff = after - before;
  if (diff === 0) return null;
  return {
    direction: diff > 0 ? "up" : "down",
    amount: Math.abs(diff),
    isFirstTime: false,
  };
}

// Pricing Status filter options. Live data carries only "success", "failed" and
// null today; "Success (Delayed)" and "Pending" are added by a separate ticket,
// so they are listed here (the design specifies all four) and simply match
// nothing until that ships — no second change to this filter needed.
const PH_PRICING_STATUSES = [
  { id: "success", name: "Success" },
  { id: "success_delayed", name: "Success (Delayed)" },
  { id: "failed", name: "Failed" },
  { id: "pending", name: "Pending" },
];

// Buckets a raw sync_status into one of the four options above.
// An unknown or null status deliberately lands in "failed": that is what the
// table already paints red (see platformSynced, which tests === "success"), and
// the filter must never disagree with the icon sitting next to it. 24 of the
// platform rows on dev carry a null status for exactly this reason.
function phStatusKey(raw, platformName = "") {
  const v = String(raw || "").toLowerCase().trim();
  if (v === "success") return "success";
  if (v.includes("delay")) return "success_delayed";
  // ERP-5170: a Lazada campaign price lock is NOT a failure. The price is saved
  // in ERP and retry_lazada_locked_prices pushes it once the campaign ends, so
  // it belongs with the other prices that have not reached their platform yet.
  // Before this it fell through to "failed" and the Failed filter listed it.
  //
  // Pending is LAZADA ONLY (Jess, 17 Sep). Nothing retries a pending push on any
  // other platform — there is no non-Lazada retry_lazada_locked_prices and no
  // _mark_delayed_price_history to close the row afterwards — so a pending row
  // there was a promise the ERP could not keep, and it wore a Campaign tag for a
  // campaign that did not exist. The backend no longer writes them; the ones that
  // remain were written before that, or come from the (listing, platform) status
  // fallback picking up some other mapping's row. Either way the honest answer to
  // "did my price go out?" is no, and the user should re-save — which is what the
  // Failed bucket says.
  if (v === "pending" || v === "locked") {
    return isLazadaPlatform(platformName) ? "pending" : "failed";
  }
  return "failed";
}

function mapPricingHistoryApiToUi(history) {
  return {
    id: history.sales_listing_price_history_id,
    time: history.changes_time,
    initiator: { brandName: history.initiator },
    reason: history.reason,
    changeCount: history.items.length,
    changes: history.items.map((item, index) => ({
      id: `${history.sales_listing_price_history_id}-${index}`,
      stockName: item.spu,
      variant: item.sku_name || item.sku || "-",
      model_number: item.model_number || "",
      condition: item.condition || "-",
      grading: item.condition_type || "-",
      setType: item.set_type || "",
      avatar: item.sku_image || noImage,
      baseBefore: (() => {
        const before = item.base_price_before;
        const after = item.base_price_after;
        const beforeEmpty = before == null || before === "" || Number(before) === 0;
        const afterEmpty = after == null || after === "" || Number(after) === 0;
        if (beforeEmpty && afterEmpty) return "-"; // no change, both blank
        if (!beforeEmpty && !afterEmpty && Number(before) === Number(after)) return "-"; // no change, same value
        if (beforeEmpty) return "BLANK"; // was unset, now has value
        return `SGD ${Number(before).toFixed(2)}`;
      })(),
      baseAfter: (() => {
        const before = item.base_price_before;
        const after = item.base_price_after;
        const beforeEmpty = before == null || before === "" || Number(before) === 0;
        const afterEmpty = after == null || after === "" || Number(after) === 0;
        if (beforeEmpty && afterEmpty) return "-"; // no change, both blank
        if (!beforeEmpty && !afterEmpty && Number(before) === Number(after)) return "-"; // no change, same value
        if (afterEmpty) return "BLANK"; // was set, now cleared
        return `SGD ${Number(after).toFixed(2)}`;
      })(),
      listing_code: item.listing_code,
      platforms: (item.platforms || []).map((p) => {
        const before = Number(p.platform_price_before);
        const after = Number(p.platform_price_after);
        const isFirstTime =
          p.platform_price_before === null || p.platform_price_before === undefined || p.platform_price_before === "";
        const noChange = !isFirstTime && before === after;
        return noChange ? null : {
          name: p.platform,
          beforePrice: isFirstTime ? "-" : `SGD ${before.toFixed(2)}`,
          afterPrice: `SGD ${after.toFixed(2)}`,
          syncStatus: p.sync_status,
          // ERP-5170: when a campaign-locked push finally reached the platform.
          appliedAt: p.applied_at || null,
          // ERP enhancement #2 — one stock can have several listings on the same
          // platform, so the platform name alone does not say which was repriced.
          productName: p.platform_product_name || "",
          skuName: p.platform_sku_name || "",
          productId: p.platform_product_id || "",
          skuId: p.platform_sku_id || "",
        };
      }).filter(Boolean),
    })),
  };
}

export default function PlatformPricing() {
  const breadcrumbs = ["Pricing", "Platform Pricing"];
  const history = useHistory();
  const location = useLocation();
  const { showLoader, hideLoader } = useLoader();

  // Tab
  const [activeTab, setActiveTab] = useState("initiate-pricing");

  useEffect(() => {
    const incomingTab = location?.state?.activeTab;
    if (incomingTab) {
      setActiveTab(incomingTab);
      history.replace(location.pathname, {});
    }
  }, []); // eslint-disable-line

  // ── Resizable columns for the ppv2-table (Initiate Pricing tab) ──
  // This page's table is hand-rolled (not the shared DynamicTable component),
  // so resize state/handlers are self-contained here rather than reused from
  // DynamicTable.js. table-layout:fixed is required for the dragged width to
  // actually stick — table-layout:auto lets content override a shrunk <th>
  // width, which is the same issue DynamicTable's own resizableColumns work
  // had to solve.
  const [colWidths, setColWidths] = useState({
    stock: 250,   // holds the checkbox too since ERP enhancement #1
    costPrice: 140,
    platforms: 190,  // 160 truncated "WooCommerce" once the Delist pill joined the line: dot 8 + gap 8 + name 91 + gap 8 + pill 39 = 154 > 140 of content
    basePrice: 140,
    platformPrice: 140,
    notes: 150,
  });

  // Only show the sticky-column edge shadow when there's actually scrolled
  // content hidden behind it, not when the table sits flush/unscrolled.
  const ppv2ScrollRef = useRef(null);
  const [ppv2ScrolledLeft, setPpv2ScrolledLeft] = useState(false);
  const [ppv2ScrolledRight, setPpv2ScrolledRight] = useState(false);
  const handlePpv2Scroll = () => {
    const el = ppv2ScrollRef.current;
    if (!el) return;
    setPpv2ScrolledLeft(el.scrollLeft > 0);
    setPpv2ScrolledRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };

  const handleColResizeMouseDown = (e, key, startWidth, minWidth = 80) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;

    const onMouseMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX;
      setColWidths((prev) => ({ ...prev, [key]: Math.max(minWidth, startWidth + delta) }));
    };
    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const ColResizeHandle = ({ colKey, minWidth }) => (
    <div
      className="col-resize-handle"
      onMouseDown={(e) => handleColResizeMouseDown(e, colKey, colWidths[colKey], minWidth)}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: "absolute",
        right: 0,
        top: 0,
        height: "100%",
        width: "6px",
        cursor: "col-resize",
        zIndex: 2,
        userSelect: "none",
      }}
    />
  );

  // Resizable columns for the Pricing History tab's table (separate widths
  // from the main listing table above).
  const [phColWidths, setPhColWidths] = useState({
    changeTime: 150,
    initiator: 130,
    stock: 220,
    basePriceChanges: 200,
    platform: 260,   // widened for ERP enhancement #2: now carries platform + product + SKU
    platformPriceChanges: 200,
  });

  const handlePhColResizeMouseDown = (e, key, startWidth, minWidth = 90) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const onMouseMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX;
      setPhColWidths((prev) => ({ ...prev, [key]: Math.max(minWidth, startWidth + delta) }));
    };
    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const PhColResizeHandle = ({ colKey, minWidth }) => (
    <div
      className="col-resize-handle"
      onMouseDown={(e) => handlePhColResizeMouseDown(e, colKey, phColWidths[colKey], minWidth)}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: "absolute",
        right: 0,
        top: 0,
        height: "100%",
        width: "6px",
        cursor: "col-resize",
        zIndex: 2,
        userSelect: "none",
      }}
    />
  );

  // Data
  const [listings, setListings] = useState([]);

  // Platform Price follows its longest value on the page. The table is
  // table-layout:fixed and .entities-table td clips with an ellipsis, so at the
  // 140px default a Lazada row's "SGD 3,278.00 SGD 3,279.00 (i)" was cut to
  // "SGD 3,278.00 SGD 32". Widen-only: a narrower drag by the user stands until
  // the next page of data arrives.
  useEffect(() => {
    const needed = parseInt(getContentColumnWidth(
      listings.flatMap((l) => l.platforms || []),
      (p) => {
        if (!p.erpPrice || p.erpPrice === "-") return "";
        const { priceText, normalText } = erpPriceParts(p);
        return normalText ? `${priceText} ${normalText}` : priceText;
      },
      { label: "Platform Price", extra: PLATFORM_PRICE_CELL_EXTRA, min: 140, max: 480 }
    ), 10);
    setColWidths((prev) => (needed > prev.platformPrice ? { ...prev, platformPrice: needed } : prev));
  }, [listings]);
  // ERP-5173: edge shadows must be right on first paint, after a column
  // resize and after a viewport resize — not only once the user scrolls.
  useEffect(() => {
    handlePpv2Scroll();
    window.addEventListener("resize", handlePpv2Scroll);
    return () => window.removeEventListener("resize", handlePpv2Scroll);
  }, [listings, colWidths, activeTab]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  // Search / Filter
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // ERP-5233 two-box search — same three stages as the single box it replaces:
  // typed (*Input) -> committed -> debounced, which is what reaches the API.
  // Pricing History keeps its own SearchBox; it searches a different dataset.
  const [modelSearchInput, setModelSearchInput] = useState("");
  // The committed model term travels with the flag that says how to match it.
  // `exact` is set only when the term came out of the typeahead, where every
  // entry is a model name that exists — picking "iPhone 15" there means iPhone
  // 15, not iPhone 15 Pro. Typing your own words and pressing Enter stays broad.
  const [modelSearch, setModelSearch] = useState({ term: "", exact: false });
  const [debouncedModelSearch, setDebouncedModelSearch] = useState({ term: "", exact: false });
  // Refetches after the first paint dim the table instead of covering the screen.
  const [tableLoading, setTableLoading] = useState(false);
  const isFirstLoad = useRef(true);
  const loadSeq = useRef(0);
  const [detailSearchInput, setDetailSearchInput] = useState("");
  const [detailSearch, setDetailSearch] = useState("");
  const [debouncedDetailSearch, setDebouncedDetailSearch] = useState("");
  const [sortField] = useState("modified_at");
  const [sortOrder] = useState("desc");
  const [filterPopupOpen, setFilterPopupOpen] = useState(false);
  const [filters, setFilters] = useState({ status: [], platforms: [], setTypes: [] });
  const [tempValues, setTempValues] = useState({ status: [], platforms: [], setTypes: [] });
  const filterButtonRef = useRef(null);

  // Expand
  const [expandedStocks, setExpandedStocks] = useState({});

  // Selection / Bulk Pricing
  const [selectedIds, setSelectedIds] = useState([]);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);

  // Selection spans pages, so every membership test below goes through this Set
  // rather than selectedIds.includes(). A whole-catalogue select-all puts tens
  // of thousands of ids in here, and `.includes()` inside a `.every()` over the
  // same order of magnitude is quadratic - it locks the tab rather than
  // rendering slowly, which is the kind of thing that looks like a hang.
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  // Every selectable mapping id in the whole filtered result set, held only
  // once the user asks for it and tagged with the filter signature it belongs
  // to: the moment the search or filters change, the old set is the wrong
  // answer and must not be reused. Ids only - the rows come later, and only
  // for what was actually selected (see handleStartBulkPricing).
  const [allSelectableIds, setAllSelectableIds] = useState(null);
  const [allSelectableKey, setAllSelectableKey] = useState(null);
  const [loadingAll, setLoadingAll] = useState(false);

  // Inline price editing
  const [editCell, setEditCell] = useState(null);
  // { listingId, field: 'base_price' | 'platform_price', mappingId, platformId, value, anchorRect }
  const [editError, setEditError] = useState(false);
  // How many listings the in-flight save covers; 0 when nothing is saving. A
  // count rather than a flag because the progress banner reports "0 / N", and a
  // group base-price edit writes every listing in the group, not just one.
  const [savingCount, setSavingCount] = useState(0);

  // Notification
  const [notification, setNotification] = useState(null);

  // Pricing History
  const [pricingHistory, setPricingHistory] = useState([]);
  const [phSearchTerm, setPhSearchTerm] = useState("");
  const [phSearchInput, setPhSearchInput] = useState("");
  const [phCurrentPage, setPhCurrentPage] = useState(1);
  const [phSortField, setPhSortField] = useState("changeTime");
  const [phSortOrder, setPhSortOrder] = useState("desc");
  const [phFilterPopupOpen, setPhFilterPopupOpen] = useState(false);
  const [phAppliedFilter, setPhAppliedFilter] = useState(null);
  const [phTempValues, setPhTempValues] = useState({ changesTime_start: null, changesTime_end: null, initiator: [], pricingObject: [], platform: [], pricingStatus: [] });

  // Prefetch
  const prefetchCache = useRef(new Map());
  const prefetchInProgress = useRef(new Set());
  const isFirstSearchRender = useRef(true);
  const isFirstFilterRender = useRef(true);

  // See the same pair in PlatformListingTest — one object so a third box later
  // does not mean widening every signature between here and the fetch.
  function appendSearchParams(params, sq) {
    if (sq?.q?.trim()) params.append("search", sq.q.trim());
    if (sq?.model?.trim()) {
      params.append("model_search", sq.model.trim());
      if (sq.modelExact) params.append("model_search_exact", "true");
    }
    if (sq?.detail?.trim()) params.append("detail_search", sq.detail.trim());
    return params;
  }

  function buildCacheKey(page, sq, flt, sf, so) {
    return JSON.stringify({
      page,
      search: (sq?.q || "").trim(),
      model: (sq?.model || "").trim(),
      // The flag only counts when a term is actually typed, or an empty box would
      // key differently depending on how it was last emptied.
      modelExact: (sq?.model || "").trim() ? !!sq?.modelExact : false,
      detail: (sq?.detail || "").trim(),
      flt, sf, so,
    });
  }

  function buildOrdering(sf, so) {
    return `${so === "desc" ? "-" : ""}${sf}`;
  }

  async function prefetchPage(page, sq, flt, sf, so, totalCnt) {
    const totalPgs = Math.ceil(totalCnt / PAGE_SIZE);
    if (page > totalPgs) return;
    const key = buildCacheKey(page, sq, flt, sf, so);
    if (prefetchCache.current.has(key) || prefetchInProgress.current.has(key)) return;
    prefetchInProgress.current.add(key);
    try {
      const params = new URLSearchParams({ page, page_size: PAGE_SIZE });
      appendSearchParams(params, sq);
      if (flt?.status?.length) params.append("status", flt.status.join(","));
      if (flt?.platforms?.length) params.append("platform", flt.platforms.join(","));
      if (flt?.setTypes?.length) params.append("set_type", flt.setTypes.join(","));
      params.append("ordering", buildOrdering(sf, so));
      const res = await apiHelper(`sales/initiate-pricing-v2/?${params.toString()}`, "GET");
      if (!res.ok) return;
      const json = await res.json();
      prefetchCache.current.set(key, {
        results: (json.results || []).map(mapApiToUi),
        count: json.count || 0,
      });
    } catch (_) {
    } finally {
      prefetchInProgress.current.delete(key);
    }
  }

  // The three terms as one value: the fetch, the cache key, the prefetch and the
  // post-save refresh all read this rather than three separate pieces of state.
  // Pulled apart so the fetch effect's dependency array stays statically
  // checkable — a member expression in there trips react-hooks/exhaustive-deps.
  const modelTerm = debouncedModelSearch.term;
  const modelExact = debouncedModelSearch.exact;
  const anySearchActive = !!debouncedSearch || !!modelTerm || !!debouncedDetailSearch;

  const searchQuery = {
    q: debouncedSearch,
    model: modelTerm,
    modelExact,
    detail: debouncedDetailSearch,
  };

  // Identifies "which result set", independent of which page of it is showing.
  // buildCacheKey with the page pinned to 0 gives exactly that, and reusing it
  // means the signature can never drift from the one the table pages by.
  const selectionKey = useMemo(
    () => buildCacheKey(0, searchQuery, filters, sortField, sortOrder),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debouncedSearch, modelTerm, modelExact, debouncedDetailSearch, filters, sortField, sortOrder]
  );

  // Every selectable mapping id in the whole result set. Returns the cached
  // set when it still belongs to the current filters.
  //
  // This does NOT fetch rows. sales/pricing-mapping-ids/ runs the same filter
  // pipeline as the table (build_pricing_listings_qs on the backend, shared on
  // purpose) and answers with five fields per mapping - all the FAILED verdict
  // needs. Measured on live 2026-09-18, unfiltered catalogue: 20,801 mappings
  // in 0.66s / 1.07MB, against 15.5s / 16.2MB for the full rows at page_size=0.
  //
  // Same search/filter/ordering parameters as the table's own fetch below - a
  // different parameter here would silently select a different set than the
  // one on screen.
  //
  // The verdict itself stays in the browser: getPlatformSyncStatus is the one
  // place that decides what "FAILED" means, and it is fed exactly the shape
  // mapApiToUi builds for the table, so select-all and the row checkboxes
  // cannot disagree.
  const fetchSelectableIds = async () => {
    if (allSelectableIds && allSelectableKey === selectionKey) return allSelectableIds;
    setLoadingAll(true);
    try {
      const params = new URLSearchParams();
      appendSearchParams(params, searchQuery);
      if (filters?.status?.length) params.append("status", filters.status.join(","));
      if (filters?.platforms?.length) params.append("platform", filters.platforms.join(","));
      if (filters?.setTypes?.length) params.append("set_type", filters.setTypes.join(","));
      params.append("ordering", buildOrdering(sortField, sortOrder));
      const res = await apiHelper(`sales/pricing-mapping-ids/?${params.toString()}`, "GET");
      if (!res.ok) throw new Error("Failed to fetch the selectable set");
      const json = await res.json();
      // Columns are read by NAME from `fields`, never by position. The first
      // version destructured [id, name, price, override, status] while the
      // server happened to emit status before override: the two columns were
      // read swapped, every row was judged FAILED, and select-all ticked
      // nothing - "all 0" on screen and no error anywhere. A shape this code
      // does not recognise now fails loudly instead of selecting nothing.
      const col = Object.fromEntries((json.fields || []).map((f, i) => [f, i]));
      const need = ["sales_listing_mapping_id", "platform_name", "platform_price", "override_price", "sync_status"];
      const missing = need.filter((f) => !(f in col));
      if (missing.length) {
        throw new Error(`pricing-mapping-ids: missing columns ${missing.join(", ")} in ${JSON.stringify(json.fields)}`);
      }
      const ids = (json.rows || [])
        .filter((r) =>
          getPlatformSyncStatus({
            name: r[col.platform_name],
            platformPrice: formatSGD(r[col.platform_price]),
            erpPrice: formatSGD(r[col.override_price]),
            syncstatus: r[col.sync_status],
          }) !== "FAILED"
        )
        .map((r) => r[col.sales_listing_mapping_id]);
      setAllSelectableIds(ids);
      setAllSelectableKey(selectionKey);
      return ids;
    } catch (err) {
      console.error(err);
      setNotification({ type: "error", message: "Could not load the full list — please try again" });
      return null;
    } finally {
      setLoadingAll(false);
    }
  };

  useEffect(() => {
    async function load() {
      const key = buildCacheKey(currentPage, searchQuery, filters, sortField, sortOrder);
      const cached = prefetchCache.current.get(key);
      if (cached) {
        setListings(cached.results);
        setTotalCount(cached.count);
        prefetchPage(currentPage + 1, searchQuery, filters, sortField, sortOrder, cached.count);
        return;
      }
      // The global loader is a full-screen opaque overlay — right for the first
      // paint of the page, far too heavy for a search/sort/filter refetch. Those
      // keep the table on screen and dim it instead.
      const seq = ++loadSeq.current;
      if (isFirstLoad.current) showLoader(); else setTableLoading(true);
      try {
        const params = new URLSearchParams({ page: currentPage, page_size: PAGE_SIZE });
        appendSearchParams(params, searchQuery);
        if (filters?.status?.length) params.append("status", filters.status.join(","));
        if (filters?.platforms?.length) params.append("platform", filters.platforms.join(","));
        if (filters?.setTypes?.length) params.append("set_type", filters.setTypes.join(","));
        params.append("ordering", buildOrdering(sortField, sortOrder));
        const res = await apiHelper(`sales/initiate-pricing-v2/?${params.toString()}`, "GET");
        if (!res.ok) throw new Error("Failed to fetch");
        const json = await res.json();
        // A slower earlier request must not overwrite a newer one's rows.
        if (seq !== loadSeq.current) return;
        const mapped = (json.results || []).map(mapApiToUi);
        prefetchCache.current.set(key, { results: mapped, count: json.count || 0 });
        setListings(mapped);
        setTotalCount(json.count || 0);
        prefetchPage(currentPage + 1, searchQuery, filters, sortField, sortOrder, json.count || 0);
      } catch (err) {
        console.error(err);
      } finally {
        if (seq === loadSeq.current) {
          if (isFirstLoad.current) { isFirstLoad.current = false; hideLoader(); }
          else setTableLoading(false);
        }
      }
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearch, modelTerm, modelExact, debouncedDetailSearch,
      filters, sortField, sortOrder]);

  useEffect(() => {
    if (isFirstSearchRender.current) { isFirstSearchRender.current = false; return; }
    const t = setTimeout(() => { setCurrentPage(1); setDebouncedSearch(searchTerm); }, 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    const t = setTimeout(() => { setCurrentPage(1); setDebouncedModelSearch(modelSearch); }, 300);
    return () => clearTimeout(t);
  }, [modelSearch]);

  useEffect(() => {
    const t = setTimeout(() => { setCurrentPage(1); setDebouncedDetailSearch(detailSearch); }, 300);
    return () => clearTimeout(t);
  }, [detailSearch]);

  useEffect(() => {
    if (isFirstFilterRender.current) { isFirstFilterRender.current = false; return; }
    setCurrentPage(1);
    prefetchCache.current.clear();
    prefetchInProgress.current.clear();
  }, [filters, sortField, sortOrder]); // eslint-disable-line

  useEffect(() => {
    if (location.state?.notification) {
      setNotification(location.state.notification);
      history.replace({ ...location, state: {} });
    }
  }, []); // eslint-disable-line


  // Close price popover on outside click
  useEffect(() => {
    if (!editCell) return;
    const handler = (e) => {
      // Close if click is not inside the popover itself
      if (!e.target.closest(".ppv2-popover") && !e.target.closest(".ppv2-edit-btn")) {
        setEditCell(null);
        setEditError(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [editCell]);

  const grouped = useMemo(() => groupByStock(listings), [listings]);
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const isTableEmpty = listings.length === 0;
  const filtersApplied = (filters.platforms || []).length > 0 || (filters.status || []).length > 0 || (filters.setTypes || []).length > 0;
  const isSearching = anySearchActive;
  const isFilterApplied = filtersApplied;
  const isInitiatePricingToolbarEmpty = isTableEmpty && !isSearching && !isFilterApplied;

  // Auto-expand all stock groups when search or filter is active
  useEffect(() => {
    if (anySearchActive || filtersApplied) {
      const expanded = {};
      grouped.forEach((g) => { expanded[g.key] = true; });
      setExpandedStocks(expanded);
    }
  }, [anySearchActive, filtersApplied, grouped]); // eslint-disable-line

  // Selection helpers — operate at platform-mapping level (sales_listing_mapping_id)
  //
  // A FAILED mapping is not selectable, and that verdict comes from
  // getPlatformSyncStatus, which derives it in the browser from the platform
  // and ERP prices and the raw status. It is deliberately read here through the
  // same mapped rows the table renders, so "what the checkbox allows" and "what
  // select-all picks" cannot answer differently.
  const selectableOf = (rows) =>
    rows.flatMap((l) =>
      l.platforms.filter((p) => p.syncStatus !== "FAILED").map((p) => p.sales_listing_mapping_id)
    );

  // This page's selectable mappings — still what the group checkboxes and the
  // "N on this page" label work from.
  const allMappingIds = useMemo(() => selectableOf(listings), [listings]); // eslint-disable-line react-hooks/exhaustive-deps

  // The whole filtered set's, once fetched and still current. Already
  // filtered to selectable by fetchSelectableIds.
  const fullMappingIds =
    (allSelectableIds && allSelectableKey === selectionKey) ? allSelectableIds : null;

  // The header checkbox means the whole filtered set. Its DISPLAY is judged
  // against that set when it is cached and falls back to this page when it is
  // not - a guess, and the only one available short of fetching on every
  // filter change. Its ACTION never relies on that fallback: toggleAll resolves
  // the full set before deciding anything. When only part of what is on hand is
  // selected the box shows indeterminate, the same way the group checkboxes
  // do, rather than a firm yes or no read off a single page.
  const selectableIds = fullMappingIds ?? allMappingIds;
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedSet.has(id));
  const headerIndeterminate = !allSelected && selectableIds.some((id) => selectedSet.has(id));

  const toggleAll = async () => {
    // BOTH directions act on the whole result set, so resolve it first - cached
    // when it already belongs to the current filters, one call otherwise.
    //
    // Untick used to drop `selectableIds`, which falls back to the current PAGE
    // whenever the cached set belongs to an older filter. So: select all Lazada
    // (1,189), move on to other filters, come back to Lazada, untick - and only
    // the 20 rows on screen came off: 4,410 became 4,390 instead of 3,221
    // (live, 2026-09-18). Tick never had the problem because it always fetched.
    const ids = await fetchSelectableIds();
    if (!ids) return;
    const everySelected = ids.length > 0 && ids.every((id) => selectedSet.has(id));
    if (everySelected) {
      // Untick drops this result set only. Selections made under a different
      // search or filter are left alone — they were never shown here, so
      // clearing them would be silent data loss of the user's own work.
      const drop = new Set(ids);
      setSelectedIds((prev) => prev.filter((id) => !drop.has(id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...ids])]);
    }
  };

  const clearSelection = () => setSelectedIds([]);

  const toggleExpand = (groupKey) => {
    setExpandedStocks((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  // Filter sections
  const filterSections = [
    {
      label: "API Status",
      type: "checkbox",
      key: "status",
      options: ["Connect", "Disconnect"],
      divider: true,
    },
    {
      label: "Platform",
      type: "list",
      key: "platforms",
      data: ["Shopee SG", "Lazada SG", "TikTok SG", "WooCommerce"],
      showDropdownIcon: true,
      renderItem: (name) => ({ id: name, title: name, subtitle: "", image: null }),
      optionHoverBg: "#F8F9FA",
    },
    // ERP-5160 - the Stock page's Set Type filter widget (chip box + dropdown)
    // over the three values Stock.set_type takes; "None" also covers a blank.
    {
      label: "Set Type",
      type: "list",
      key: "setTypes",
      data: SET_TYPE_OPTIONS,
      renderItem: (name) => ({ id: name, title: name, subtitle: "", image: null }),
      showDropdownIcon: true,
      optionHoverBg: "#F8F9FA",
    },
  ];

  // Inline price save flow
  const startEdit = (e, listingId, field, mappingId, platformId, currentValue, groupListings = null) => {
    e.stopPropagation();
    const btn = e.currentTarget;
    const rect = btn.getBoundingClientRect();
    const num = currentValue != null ? Number(currentValue) : "";
    setEditError(false);
    setEditCell({ listingId, field, mappingId, platformId, value: isNaN(num) ? "" : String(num === 0 ? "" : num), anchorRect: rect, groupListings });
  };

  const cancelEdit = (e) => {
    if (e) e.stopPropagation();
    setEditCell(null);
    setEditError(false);
  };

  // Lazada rejects a Special Price above the item's Normal (list) price.
  // Shared by commitEdit and the input's onBlur, so the error shows as soon
  // as the user leaves the field, not only on Save.
  const getLazadaMaxPriceError = (listingId, mappingId, newVal) => {
    if (newVal == null || isNaN(newVal)) return null;
    const listingForCheck = listings.find((l) => l.id === listingId);
    const platformForCheck = listingForCheck?.platforms.find(
      (p) => p.sales_listing_mapping_id === mappingId
    );
    if (
      isLazadaPlatform(platformForCheck?.name) &&
      platformForCheck?.normalPriceRaw != null &&
      newVal > platformForCheck.normalPriceRaw
    ) {
      return `Cannot exceed ${formatSGD(platformForCheck.normalPriceRaw)}`;
    }
    return null;
  };

  const commitEdit = async (e) => {
    if (e) e.stopPropagation();
    if (!editCell) return;
    const trimmed = editCell.value.trim();
    const isBaseField = editCell.field === "base_price" || editCell.field === "base_price_group";
    // Base price is optional — allow empty. Platform price is required.
    if (trimmed === "" && !isBaseField) { setEditError(true); return; }
    const newVal = trimmed === "" ? null : parseFloat(trimmed);
    if (newVal !== null && (isNaN(newVal) || newVal < 0)) { setEditError(true); return; }

    if (newVal !== null && !isBaseField) {
      const err = getLazadaMaxPriceError(editCell.listingId, editCell.mappingId, newVal);
      if (err) { setEditError(err); return; }
    }
    setEditError(false);

    let payload;
    if (editCell.field === "base_price_group") {
      // Apply new base price to every listing in the group
      payload = (editCell.groupListings || []).map((l) => ({
        listing_id: l.id,
        prices: l.platforms.map((p) => ({
          sales_platform_id: p.id,
          sales_listing_mapping_id: p.sales_listing_mapping_id,
          base_price: newVal, // null when cleared
          override_price: p.override_price_raw != null ? Number(p.override_price_raw) : null,
        })),
      }));
    } else {
      const listing = listings.find((l) => l.id === editCell.listingId);
      if (!listing) { setEditCell(null); return; }
      let prices;
      if (editCell.field === "base_price") {
        prices = listing.platforms.map((p) => ({
          sales_platform_id: p.id,
          sales_listing_mapping_id: p.sales_listing_mapping_id,
          base_price: newVal, // null when cleared
          override_price: p.override_price_raw != null ? Number(p.override_price_raw) : null,
        }));
      } else {
        // base_price is deliberately absent: this edit is about the platform
        // price. It used to send the listing's *displayed* base price back, so a
        // listing showing "-" had its stored base price blanked on every platform
        // row - and that "-" was often only a display artefact. The backend
        // leaves base_price untouched when the key is omitted, and still honours
        // an explicit null from the two Base Price cells above as a real clear.
        prices = listing.platforms.map((p) => ({
          sales_platform_id: p.id,
          sales_listing_mapping_id: p.sales_listing_mapping_id,
          override_price:
            p.sales_listing_mapping_id === editCell.mappingId
              ? newVal
              : p.override_price_raw != null ? Number(p.override_price_raw) : null,
        }));
      }
      payload = [{ listing_id: editCell.listingId, prices }];
    }

    setEditCell(null);
    setSavingCount(payload.length);
    try {
      const body = { reason: "Price update", listings: payload };
      const res = await apiHelper("sales/pricing/bulk-pricing/", "POST", body);
      if (!res.ok) throw new Error("Save failed");
      // ERP-5170: the save always succeeded in ERP, so the toast is always the
      // green one. What varies is the breakdown under it — Success / Failed /
      // Pending, where Pending is a Lazada campaign lock or a deferred push, both
      // of which go out on their own. It used to raise a warning toast for a
      // campaign lock, which read like something had gone wrong.
      const saved = await res.json().catch(() => ({}));
      prefetchCache.current.clear();
      prefetchInProgress.current.clear();
      const params = new URLSearchParams({ page: currentPage, page_size: PAGE_SIZE });
      appendSearchParams(params, searchQuery);
      if (filters?.status?.length) params.append("status", filters.status.join(","));
      if (filters?.platforms?.length) params.append("platform", filters.platforms.join(","));
      if (filters?.setTypes?.length) params.append("set_type", filters.setTypes.join(","));
      params.append("ordering", buildOrdering(sortField, sortOrder));
      const refreshRes = await apiHelper(`sales/initiate-pricing-v2/?${params.toString()}`, "GET");
      if (refreshRes.ok) {
        const json = await refreshRes.json();
        setListings((json.results || []).map(mapApiToUi));
        setTotalCount(json.count || 0);
      }
      // Raised only once the refreshed row is on screen: the progress banner and
      // this toast share one slot, so handing over any earlier would announce the
      // save while the table still showed the old price.
      setNotification({
        type: "success",
        message: "Pricing Saved Successfully",
        // counts, not the rendered element: Bulk Pricing hands its toast over
        // through router state, which the browser structured-clones, and a React
        // element does not survive that. Both paths carry data and the render
        // site below builds the block.
        counts: saved.counts || null,
      });
    } catch (err) {
      console.error(err);
      setNotification({ type: "danger", message: "Failed to update price" });
    } finally {
      setSavingCount(0);
    }
  };

  // Bulk Pricing navigation
  //
  // The selection spans pages, so the rows it names are not all in `listings`.
  // This used to build from `listings` alone: tick a page, turn the page, press
  // Bulk Pricing, and only the page you happened to be standing on came
  // through - the rest were dropped by clearSelection() with no error and
  // nothing on screen to say so. Build from the full set whenever the selection
  // reaches beyond this page.
  const handleStartBulkPricing = async () => {
    if (selectedIds.length === 0) return;

    // Fetch exactly the selected mappings' listings. Unpaginated on purpose:
    // Bulk Pricing's bulk-fill and Save work on the whole selection
    // client-side, so it needs every row in memory and paginates only what it
    // renders. There is no cap on the selection (Jess, 18 Sep): Save posts it
    // all in one request, and any push that does not complete inside the
    // worker timeout is left for the product scan to reconcile from
    // override != platform price, the same as before.
    setLoadingAll(true);
    let source;
    try {
      const res = await apiHelper("sales/pricing-by-mappings/", "POST", { mapping_ids: selectedIds });
      if (!res.ok) throw new Error("Failed to fetch the selected listings");
      const json = await res.json();
      source = (Array.isArray(json) ? json : (json.results || [])).map(mapApiToUi);
    } catch (err) {
      console.error(err);
      setNotification({ type: "error", message: "Could not load the selected listings — please try again" });
      return;   // keep the selection so the user can retry
    } finally {
      setLoadingAll(false);
    }

    const bulkItems = source
      .map((l) => {
        const selectedPlatforms = l.platforms.filter((p) => selectedSet.has(p.sales_listing_mapping_id));
        if (selectedPlatforms.length === 0) return null;
        // `allPlatforms`: the full mapping list rides along with the selection, so
        // Bulk Pricing can still tell that Base Price is WooCommerce-owned when the
        // user ticked only the Shopee rows of a stock that is also on Woo. Bulk
        // Pricing edits and saves `platforms` only; it never writes `allPlatforms`.
        return { ...l, platforms: selectedPlatforms, allPlatforms: l.platforms };
      })
      .filter(Boolean);
    clearSelection();
    history.push("/platform-pricing/bulk-pricing", { bulkItems });
  };

  // Pricing History fetch — full data set; search/filter/sort run client-side
  const fetchPricingHistory = async () => {
    try {
      const res = await apiHelper("sales/pricing-history/", "GET");
      if (!res.ok) throw new Error("Failed to fetch pricing history");
      const data = await res.json();
      const list = Array.isArray(data) ? data : (data.results || []);
      setPricingHistory(list.map(mapPricingHistoryApiToUi));
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchPricingHistory();
  }, []); // eslint-disable-line

  const phInitiators = useMemo(() => {
    const seen = new Set();
    return (pricingHistory || []).reduce((acc, r) => {
      const name = r.initiator?.brandName;
      if (name && !seen.has(name)) { seen.add(name); acc.push({ id: name, name }); }
      return acc;
    }, []);
  }, [pricingHistory]);

  // Full platform list for the filter, fetched from the DB (same source as the
  // Platform Listing filter) instead of deriving it from the loaded rows
  const [phPlatforms, setPhPlatforms] = useState([]);
  useEffect(() => {
    apiHelper("sales/platforms/", "GET").then(async (res) => {
      if (res.ok) {
        const d = await res.json();
        setPhPlatforms(
          Array.isArray(d)
            ? d.map((p) => p.platform_name).filter(Boolean).map((name) => ({ id: name, name }))
            : []
        );
      }
    }).catch(() => {});
  }, []);

  // True only when there is genuinely no history (not just an empty search result)
  // Search/Filter must stay enabled whenever the user is actively searching or
  // has a filter applied, even if that search/filter yields zero results.
  const phIsSearching = (phSearchTerm || "").trim().length > 0;
  const phIsInitialEmpty =
    pricingHistory.length === 0 && !phIsSearching && !phAppliedFilter;

  const phFiltered = useMemo(() => {
    let rows = [...(pricingHistory || [])];
    // Multiple keywords (comma/space separated) — a row matches only if EVERY
    // keyword hits somewhere in it (Stock column fields + the Listing column's
    // platform / product / SKU names + initiator/reason).
    // e.g. data "7T Pro / apple-128GB / used / Suyog": "7T Pro apple" matches,
    // "8T Pro" and "7T Pro apple-64GB" do not.
    const terms = (phSearchTerm || "").toLowerCase().trim().split(/[,\s]+/).filter(Boolean);
    if (terms.length > 0) {
      rows = rows.map((r) => {
        const headerText = `${r.initiator?.brandName ?? ""} ${r.reason ?? ""}`.toLowerCase();
        const filteredChanges = r.changes?.filter((c) => {
          const listingText = (c.platforms || [])
            .map((p) => `${p.name ?? ""} ${p.productName ?? ""} ${p.skuName ?? ""} ${p.productId ?? ""} ${p.skuId ?? ""}`)
            .join(" ");
          const rowText = `${headerText} ${c.stockName ?? ""} ${c.variant ?? ""} ${c.model_number ?? ""} ${c.condition ?? ""} ${c.grading ?? ""} ${c.listing_code ?? ""} ${listingText}`.toLowerCase();
          return terms.every((w) => rowText.includes(w));
        });
        return filteredChanges?.length ? { ...r, changes: filteredChanges } : null;
      }).filter(Boolean);
    }
    if (phAppliedFilter?.initiator?.length > 0) {
      rows = rows.filter((r) => phAppliedFilter.initiator.includes(r.initiator?.brandName));
    }
    if (phAppliedFilter?.platform?.length > 0) {
      rows = rows.map((r) => {
        const filteredChanges = (r.changes || []).map((c) => {
          const filteredPlatforms = (c.platforms || []).filter((p) => phAppliedFilter.platform.includes(p.name));
          return filteredPlatforms.length > 0 ? { ...c, platforms: filteredPlatforms } : null;
        }).filter(Boolean);
        return filteredChanges.length > 0 ? { ...r, changes: filteredChanges } : null;
      }).filter(Boolean);
    }
    if (phAppliedFilter?.pricingObject?.length > 0) {
      const wantBase = phAppliedFilter.pricingObject.includes("Base Price");
      const wantPlatform = phAppliedFilter.pricingObject.includes("Platform Price");
      rows = rows.map((r) => {
        const filteredChanges = (r.changes || []).filter((c) => {
          const hasBase = c.baseBefore !== "-" || c.baseAfter !== "-";
          const hasPlatform = (c.platforms || []).length > 0;
          if (wantBase && wantPlatform) return hasBase || hasPlatform;
          if (wantBase) return hasBase;
          if (wantPlatform) return hasPlatform;
          return true;
        });
        return filteredChanges.length > 0 ? { ...r, changes: filteredChanges } : null;
      }).filter(Boolean);
    }
    // Pricing status. The dropdown stores PH_PRICING_STATUSES ids, which are the
    // status codes themselves, so this compares codes rather than display labels.
    // Base-price-only rows carry no platform push and are correctly dropped
    // while this filter is on.
    if (phAppliedFilter?.pricingStatus?.length > 0) {
      const want = new Set(phAppliedFilter.pricingStatus);
      rows = rows.map((r) => {
        const filteredChanges = (r.changes || []).map((c) => {
          const kept = (c.platforms || []).filter((p) => want.has(phStatusKey(p.syncStatus, p.name)));
          return kept.length > 0 ? { ...c, platforms: kept } : null;
        }).filter(Boolean);
        return filteredChanges.length > 0 ? { ...r, changes: filteredChanges } : null;
      }).filter(Boolean);
    }
    if (phAppliedFilter?.changesTime_start || phAppliedFilter?.changesTime_end) {
      const start = phAppliedFilter.changesTime_start ? new Date(phAppliedFilter.changesTime_start).setHours(0, 0, 0, 0) : null;
      const end = phAppliedFilter.changesTime_end ? new Date(phAppliedFilter.changesTime_end).setHours(23, 59, 59, 999) : null;
      rows = rows.filter((r) => {
        const t = new Date(r.time).getTime();
        if (start && t < start) return false;
        if (end && t > end) return false;
        return true;
      });
    }
    console.log("Fitlere",pricingHistory, phSearchTerm, phAppliedFilter)
    return rows;
  }, [pricingHistory, phSearchTerm, phAppliedFilter]);

  const flattenedPriceHistory = useMemo(() => {
    const rows = [];
    phFiltered.forEach((history) => {
      const hasBaseChange = history.changes.some(
        (c) => c.baseBefore !== "-" || c.baseAfter !== "-"
      );
      const hasBaseActualChange = history.changes.some((c) => {
        if (c.baseBefore === "-" && c.baseAfter === "-") return false;
        // "BLANK" or a value on either side means a real change occurred
        if (c.baseBefore === "BLANK" || c.baseAfter === "BLANK") return true;
        if (c.baseBefore === "-" || c.baseAfter === "-") return true;
        return c.baseBefore !== c.baseAfter;
      });

      // Determine if any platform has an actual price change
      const allChangedPlatforms = history.changes.flatMap((c) => c.platforms || []);
      const hasPlatformChange = allChangedPlatforms.length > 0;

      if (hasBaseActualChange && !hasPlatformChange) {
        // Case 1: Base price only — show ONE row per history entry (use first change's stock info)
        const first = history.changes[0];
        rows.push({
          historyId: history.id,
          time: history.time,
          initiator: history.initiator,
          reason: history.reason,
          id: first.id,
          stockName: first.stockName,
          variant: first.variant,
          model_number: first.model_number,
          condition: first.condition,
          grading: first.grading,
          setType: first.setType,
          avatar: first.avatar,
          baseBefore: first.baseBefore,
          baseAfter: first.baseAfter,
          listing_code: first.listing_code,
          platform: null,
        });
      } else {
        // Case 2/3/4: Platform price changed (with or without base price change)
        // Show one row per changed platform; each row carries its change's base info
        history.changes.forEach((change) => {
          const base = {
            historyId: history.id,
            time: history.time,
            initiator: history.initiator,
            reason: history.reason,
            id: change.id,
            stockName: change.stockName,
            variant: change.variant,
            model_number: change.model_number,
            condition: change.condition,
            grading: change.grading,
            setType: change.setType,
            avatar: change.avatar,
            baseBefore: change.baseBefore,
            baseAfter: change.baseAfter,
            listing_code: change.listing_code,
          };
          if (!change.platforms || change.platforms.length === 0) {
            rows.push({ ...base, platform: null });
          } else {
            change.platforms.forEach((p) => rows.push({ ...base, platform: p }));
          }
        });
      }
    });
    return rows;
  }, [phFiltered]);

  const phSorted = useMemo(() => {
    if (!phSortField) return flattenedPriceHistory;
    return [...flattenedPriceHistory].sort((a, b) => {
      let aVal, bVal;
      if (phSortField === "changeTime") { aVal = a.time || ""; bVal = b.time || ""; }
      else if (phSortField === "initiator") { aVal = a.initiator?.brandName || ""; bVal = b.initiator?.brandName || ""; }
      else if (phSortField === "stock") { aVal = a.stockName || ""; bVal = b.stockName || ""; }
      else if (phSortField === "platform") { aVal = a.platform?.name || ""; bVal = b.platform?.name || ""; }
      else { aVal = ""; bVal = ""; }
      if (aVal < bVal) return phSortOrder === "asc" ? -1 : 1;
      if (aVal > bVal) return phSortOrder === "asc" ? 1 : -1;
      return 0;
    });
  }, [flattenedPriceHistory, phSortField, phSortOrder]);

  const phTotalPages = Math.max(1, Math.ceil(phSorted.length / PH_PAGE_SIZE));
  const phPageData = useMemo(() => {
    const start = (phCurrentPage - 1) * PH_PAGE_SIZE;
    return phSorted.slice(start, start + PH_PAGE_SIZE);
  }, [phSorted, phCurrentPage]);

  useEffect(() => { setPhCurrentPage(1); }, [phSorted.length]); // eslint-disable-line
  useEffect(() => { setPhCurrentPage(1); }, [phSortField, phSortOrder]); // eslint-disable-line

  const handlePhSort = (field) => {
    setPhSortField((prev) => {
      if (prev === field) { setPhSortOrder((o) => (o === "asc" ? "desc" : "asc")); return field; }
      setPhSortOrder("asc"); return field;
    });
  };

  const phSections = [
    { label: "Changes Time", key: "changeTime", type: "dateRange" },
    {
      label: "Initiator", key: "initiator", type: "list",
      data: phInitiators,
      renderItem: (item) => ({ id: item.id, title: item.name }),
      showDropdownIcon: true,
    },
    { label: "Pricing Object", key: "pricingObject", type: "checkbox", options: ["Base Price", "Platform Price"] },
    {
      label: "Platform", key: "platform", type: "simpleList",
      data: phPlatforms,
      renderItem: (item) => ({ id: item.id, title: item.name }),
      showDropdownIcon: true,
    },
    {
      label: "Pricing Status", key: "pricingStatus", type: "simpleList",
      data: PH_PRICING_STATUSES,
      renderItem: (item) => ({ id: item.id, title: item.name }),
      showDropdownIcon: true,
    },
  ];

  return (
    <div className="company-body new-look stock-wrap bg-checkbox">
      <style>{`
        /* ── Card grouping (ERP enhancement #1) ──────────────────────────
           A stock and its listings read as one card. Built from the CELLS,
           not a wrapper: a wrapper would have to be a table-row ancestor and
           that breaks position:sticky on the pinned columns. Parent row is
           white and rounds the top two corners; the count strip and the
           sub-rows are grey, and whichever row ends the group rounds the
           bottom two. A transparent spacer row makes the gap between cards.
           Column resize / horizontal scroll / sticky edges are untouched. */
        .ppv2-table tbody tr { background: transparent !important; }

        /* Parent stock row — white, top of the card */
        .ppv2-stock-row td {
          background: #FFFFFF !important;
          border-top: 1px solid #DEE2E6 !important;
          border-bottom: 1px solid #DEE2E6 !important;
        }
        .ppv2-stock-row td:first-child { border-top-left-radius: 8px; }
        .ppv2-stock-row td:last-child  { border-top-right-radius: 8px; }

        /* A stock group stacks one line per platform mapping in the Platforms /
           Platform Price columns, so a heavily-mapped stock renders a row taller
           than the viewport. Cells default to middle alignment, which parks the
           checkbox, the Stock name and the Base Price edit control at that row's
           vertical centre — scrolled out of sight and impossible to click.
           Top-align them so each stays beside the row's first line. */
        .ppv2-stock-row td { vertical-align: top; }

        /* Count strip — grey, sits between the parent and its children */
        .ppv2-count-row td {
          background: #F8F9FA !important;
          border-top: none !important;
          border-bottom: 1px solid #DEE2E6 !important;
        }

        /* Sub-rows — grey, part of the card */
        .ppv2-subrow td {
          font-size: 12px;
          background: #F8F9FA !important;
          border-bottom: 1px solid #DEE2E6 !important;
          vertical-align: top;
        }

        /* The card's side edges. Every row in a group draws the left border on
           its first cell and the right border on its last, so the outline
           closes around the whole card — without them the group reads as loose
           horizontal rules, not a card. Middle cells draw neither. */
        .ppv2-stock-row td:first-child,
        .ppv2-count-row td:first-child,
        .ppv2-subrow td:first-child { border-left: 1px solid #DEE2E6 !important; }
        .ppv2-stock-row td:last-child,
        .ppv2-count-row td:last-child,
        .ppv2-subrow td:last-child { border-right: 1px solid #DEE2E6 !important; }

        /* Whichever row ends the card rounds the bottom corners —
           the count strip when collapsed, the last sub-row when not. */
        .ppv2-group-bottom td:first-child { border-bottom-left-radius: 8px; }
        .ppv2-group-bottom td:last-child  { border-bottom-right-radius: 8px; }

        /* Pricing History — rows alternate white / grey, painted on the cells
           rather than the row so it survives any per-cell background. */
        .ppv2-ph-table tbody tr:nth-child(odd) td  { background: #FFFFFF; }
        .ppv2-ph-table tbody tr:nth-child(even) td { background: #F8F9FA; }

        /* Platform name sits in a chip, not as loose bold text. */
        .ppv2-platform-chip {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          height: 16px;
          padding: 2px 6px;
          border: 1px solid #CED4DA;
          border-radius: 4px;
          background: #E9ECEF;
          color: #212529;
          font-family: Montserrat;
          font-size: 9px;
          font-weight: 700;
          line-height: 1;
          white-space: nowrap;
        }

        /* Header bar — rounded like the reference, and one uniform colour so the
           rounded ends show no seam where the sticky cells' inline #E9ECEF used
           to meet the stylesheet's #F8F9FA. */
        .ppv2-table thead th { background: #E9ECEF !important; }
        .ppv2-table thead th:first-child { border-radius: 8px 0 0 8px; }
        .ppv2-table thead th:last-child  { border-radius: 0 8px 8px 0; }

        /* 10px gap between cards */
        .ppv2-group-gap td {
          background: transparent !important;
          border: none !important;
          padding: 0 !important;
          height: 10px;
        }

        /* Child indent — ONE padding, because the checkbox now shares the Stock
           cell. It moves the checkbox, the elbow and the text right together, so
           nothing can be clipped by a column edge or painted over by the next
           sticky cell (both of which happened while the checkbox had a column of
           its own).

           The indent lines the child's CHECKBOX up with the left edge of the
           parent's THUMBNAIL (10 padding + 20 checkbox + 10 gap = 40), and the
           child's inner gap is 7 rather than 10 so the text still lands on the
           parent's text instead of 6px past it:
             child  40 + cb 20 + gap 7  + elbow 8  + gap 7  = 82
             parent 10 + cb 20 + gap 10 + thumb 32 + gap 10 = 82 */
        .ppv2-subrow td:first-child { padding-left: 40px !important; }
        .ppv2-subrow-elbow {
          color: #6C757D;
          font-size: 12px;
          line-height: 16px;
          flex-shrink: 0;
        }
        .ppv2-expand-btn { background: none; border: none; cursor: pointer; display: flex; align-items: center; gap: 4px; font-family: Montserrat; font-size: 12px; font-weight: 600; color: #6C757D; padding: 2px 0; }
        .ppv2-expand-btn:focus { outline: none; }
        .ppv2-listing-count { font-family: Montserrat; font-size: 12px; font-weight: 600; color: #6C757D; }
        .ppv2-edit-btn { background: none; border: none; cursor: pointer; color: #212529; display: inline-flex; align-items: center; padding: 2px; border-radius: 4px; }
        .ppv2-edit-btn:hover { color: #212529; background: #F8F9FA; }
        .ppv2-edit-btn:focus { outline: none; }
        .ppv2-edit-btn[disabled] { opacity: 0.4; cursor: not-allowed; }
        .ppv2-edit-btn[disabled]:hover { background: none; }
        /* "Breathing light" — ERP-5169. A listing with no WooCommerce price has no
           Base Price unless someone types one, and an empty Base Price silently
           breaks Use Base Price and Pricing Template downstream.
           Measured off the reference design, not invented: a 10px solid #CC9A06 dot
           (the same amber as the Campaign tag) sitting 6px to the right of the Base
           Price pencil, wrapped in a soft halo about 7px deep. The pencil itself
           stays plain — the first pass tinted the pencil instead, which is not what
           the design does. Only the halo animates (box-shadow), so nothing reflows:
           an animation that changed size would shift the row on a fixed-layout table. */
        @keyframes ppv2-breathe {
          0%, 100% { box-shadow: 0 0 0 2px rgba(204,154,6,0.22); }
          50%      { box-shadow: 0 0 0 7px rgba(204,154,6,0.10); }
        }
        .ppv2-breathe-dot {
          display: inline-block; width: 10px; height: 10px; border-radius: 99px;
          background: #CC9A06; flex-shrink: 0; margin-left: 2px;
          animation: ppv2-breathe 2s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .ppv2-breathe-dot { animation: none; box-shadow: 0 0 0 4px rgba(204,154,6,0.16); }
        }
        /* Price popover */
        .ppv2-popover {
          position: fixed;
          z-index: 9999;
          background: #fff;
          border-radius: 10px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.15);
          padding: 10px;
          width: 185px;
        }
        .ppv2-popover::after {
          content: '';
          position: absolute;
          bottom: -8px;
          left: 50%;
          transform: translateX(-50%);
          border-left: 8px solid transparent;
          border-right: 8px solid transparent;
          border-top: 8px solid #fff;
          filter: drop-shadow(0 2px 2px rgba(0,0,0,0.08));
        }
        .ppv2-popover-row { display: flex; align-items: center;  }
        .ppv2-sgd-prefix {
          font-size: 12px; font-family: Montserrat; color: #6C757D; font-weight: 400;
          background: #F8F9FA; border: 1px solid #6C757D; border-right: none;
          border-radius: 6px 0 0 6px; padding: 4px 5px; white-space: nowrap;
          height: 30px; display: flex; align-items: center;
        }
        .ppv2-pop-input {
          border: 1px solid #6C757D; border-radius: 0 6px 6px 0;
          padding: 4px 5px; font-size: 12px; font-family: Montserrat;
          flex: 1; min-width: 0; outline: none; height: 30px;
        }
        .ppv2-pop-input:focus { border-color: #0D6EFD; }
        .ppv2-pop-input.error { border-color: #DC3545; }
        .ppv2-sgd-prefix.error { border-color: #DC3545; }
        .ppv2-popover-actions { display: flex; align-items: center; gap: 2px; flex-shrink: 0;margin-left:3px; }
        .ppv2-pop-save { background: none; border: none; cursor: pointer; color: #495057; display: flex; align-items: center; padding: 2px; border-radius: 4px; }
        .ppv2-pop-save:hover { color: #212529; }
        .ppv2-pop-cancel { background: none; border: none; cursor: pointer; color: #6C757D; display: flex; align-items: center; padding: 2px; border-radius: 4px; }
        .ppv2-pop-cancel:hover { color: #212529; }
        .ppv2-error-msg { font-size: 11px; color: #DC3545; margin-top: 4px; font-family: Montserrat; }
        /* ERP-5169: shown while editing a WooCommerce price, because that edit also
           moves the listing's Base Price and nothing else on screen says so. */
        /* The rule above it is part of the design, not decoration: it separates the
           thing you are editing from a consequence of editing it. Sampled off the
           reference at #DEE2E6, 1px, spanning the popover's content box — which a
           border-top does for free, since the popover's own 10px padding insets it. */
        .ppv2-pop-hint {
          font-size: 10px; color: #6C757D; font-family: Montserrat; line-height: 13px;
          border-top: 1px solid #DEE2E6; margin-top: 10px; padding-top: 10px;
        }

        /* Normal Price (Lazada list price) info icon + tooltip */
        .ppv2-normprice-info { display: inline-flex; align-items: center; cursor: pointer; flex-shrink: 0; }
        .ppv2-normprice-tip {
          position: fixed; width: 309px; box-sizing: border-box; white-space: normal; background: #F8F9FA;
          color: #212529; border-radius: 8px; border: 1px solid #E9ECEF; padding: 10px; font-family: Montserrat;
          font-size: 12px; font-weight: 400; line-height: 1.4;
          z-index: 9999; pointer-events: none;
        }
        .ppv2-normprice-tip-title { font-weight: 700; margin-bottom: 4px; }

        /* Grip mark on each column's resize handle, so users can spot where to
           drag without having to hover first. */
        .col-resize-handle::after {
          content: "";
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          height: 50%;
          left: 3px;
          width: 2px;
          background: #DEE2E6;
        }

        /* Fixed-column shadow — anchored to the sticky "Stock" column itself
           so it stays correctly placed at any scroll offset, and spills onto
           the scrollable content the column is floating above. Only shown
           while something is actually scrolled behind it. Cells also need
           text-truncation ellipsis, which requires overflow:hidden — but that
           would clip this ::after pseudo-element too, so force overflow
           visible on the cell itself and let the inner text span do the
           actual clipping instead. */
        .ppv2-table td.ppv2-sticky-edge,
        .ppv2-table th.ppv2-sticky-edge {
          overflow: visible !important;
        }
        .ppv2-table.is-scrolled-left .ppv2-sticky-edge::after {
          content: "";
          position: absolute;
          top: 0;
          right: -8px;
          width: 8px;
          height: 100%;
          pointer-events: none;
          z-index: 1;
          background: linear-gradient(
            to right,
            rgba(0, 0, 0, 0.12),
            rgba(0, 0, 0, 0)
          );
        }

        /* ERP-5173: the sticky right column (Expand/Collapse). Always
           overflow:visible so the corner masks below and the shadow band can
           hang outside the padding box. */
        .ppv2-table th.ppv2-right-edge,
        .ppv2-table td.ppv2-right-edge { overflow: visible !important; }
        .ppv2-table.is-scrolled-right .ppv2-right-edge::before {
          content: "";
          position: absolute;
          top: 0;
          left: -8px;
          width: 8px;
          height: 100%;
          pointer-events: none;
          z-index: 1;
          background: linear-gradient(to left, rgba(0, 0, 0, 0.12), rgba(0, 0, 0, 0));
        }

        /* ── ERP-5173: sticky edges survive horizontal scroll ──────────────
           Borders and radii already live on the CELLS (card grouping above), so
           they travel with the sticky cell. What a cell cannot do is hide the
           column scrolling underneath it: a rounded corner leaves a notch, and
           through it the covered cell's square corner and its border line show —
           the "broken corner + stray line" seen while scrolling. A 9×9 mask in
           that notch covers it: transparent inside the curve, the border colour
           on the curve, the table surface outside. Pseudo-elements, so no markup
           changes: left cells use ::before (::after is the left shadow band),
           right cells use ::after (::before is the right shadow band).
           The outside colour is #FFFFFF, not the page grey — the table sits on
           .table-wrapper's white, and the design's --surface-card is white too. */
        .ppv2-table thead th:first-child::before,
        .ppv2-table thead th:last-child::after,
        .ppv2-table .ppv2-stock-row td:first-child::before,
        .ppv2-table .ppv2-stock-row td:last-child::after,
        .ppv2-table .ppv2-group-bottom td:first-child::before,
        .ppv2-table .ppv2-group-bottom td:last-child::after {
          content: "";
          position: absolute;
          width: 9px;
          height: 9px;
          pointer-events: none;
          z-index: 2;
        }
        /* Header bar: no border, 8px radius — one strip per side masks both corners */
        .ppv2-table thead th:first-child::before {
          top: 0; bottom: 0; left: 0; width: 8px; height: auto;
          background:
            radial-gradient(circle at 8px 8px, transparent 0 8px, #FFFFFF 8px) left top / 8px 8px no-repeat,
            radial-gradient(circle at 8px 0,   transparent 0 8px, #FFFFFF 8px) left bottom / 8px 8px no-repeat;
        }
        .ppv2-table thead th:last-child::after {
          top: 0; bottom: 0; right: 0; width: 8px; height: auto;
          background:
            radial-gradient(circle at 0 8px, transparent 0 8px, #FFFFFF 8px) right top / 8px 8px no-repeat,
            radial-gradient(circle at 0 0,   transparent 0 8px, #FFFFFF 8px) right bottom / 8px 8px no-repeat;
        }
        /* Card corners: 1px #DEE2E6 border, 8px radius. The box hangs 1px past the
           padding box so it covers the border too; the circle centre is the corner
           of the 8px radius, the 7–8px ring redraws the border across the notch. */
        .ppv2-table .ppv2-stock-row td:first-child::before {
          top: -1px; left: -1px;
          background: radial-gradient(circle at 8px 8px, transparent 0 7px, #DEE2E6 7px 8px, #FFFFFF 8px);
        }
        .ppv2-table .ppv2-stock-row td:last-child::after {
          top: -1px; right: -1px;
          background: radial-gradient(circle at 1px 8px, transparent 0 7px, #DEE2E6 7px 8px, #FFFFFF 8px);
        }
        .ppv2-table .ppv2-group-bottom td:first-child::before {
          bottom: -1px; left: -1px;
          background: radial-gradient(circle at 8px 1px, transparent 0 7px, #DEE2E6 7px 8px, #FFFFFF 8px);
        }
        .ppv2-table .ppv2-group-bottom td:last-child::after {
          bottom: -1px; right: -1px;
          background: radial-gradient(circle at 1px 1px, transparent 0 7px, #DEE2E6 7px 8px, #FFFFFF 8px);
        }
      `}</style>

      {/* Breadcrumbs */}
      <div className="breadcrumb">
        {breadcrumbs.map((crumb, i) => (
          <span key={i} className="breadcrumb-item">{crumb}{i !== breadcrumbs.length - 1}</span>
        ))}
      </div>

      {/* One slot at the top of the page: an action's progress, then its result.
          Mutually exclusive — both are pinned to the same fixed 86px offset, so
          rendering them together would stack one on top of the other. */}
      {savingCount > 0 ? (
        <LoadingNotification
          message={`Saving ${savingCount} Pricing Record${savingCount === 1 ? "" : "s"}`}
          total={savingCount}
        />
      ) : notification ? (
        <AlertNotification
          type={notification.type || "success"}
          message={notification.message}
          details={hasPricingSaveDetails(notification.counts) ? <PricingSaveSummary counts={notification.counts} /> : undefined}
          onClose={() => setNotification(null)}
        />
      ) : null}

      <div className="employee-tabs-container">
        {/* Tab Buttons */}
        <div className="tabs">
          <button
            className={`tab ${activeTab === "initiate-pricing" ? "active" : ""}`}
            onClick={() => setActiveTab("initiate-pricing")}
          >
            Initiate Pricing
          </button>
          <button
            className={`tab ${activeTab === "pricing-history" ? "active" : ""}`}
            onClick={() => { setActiveTab("pricing-history"); fetchPricingHistory(); }}
          >
            Pricing History
          </button>
        </div>

        {activeTab === "initiate-pricing" && (
        <div className="tab-content" id="listing-content">
          <div className="info-section">
            <div className="info-card" style={{ overflow: "unset" }}>
              <div className="entities-tab">

                {/* Toolbar */}
                <div className="toolbar">
                  <div className="toolbar-left" style={{ flex: 1, minWidth: 0, marginRight: '0.75rem' }}>{/* flex:1 so the two search boxes are sized by the row rather than fixed.
                                        marginRight because .toolbar is justify-content:space-between
                                        with NO gap of its own — the gap only exists inside -left/-right.
                                        While -left was content-width, space-between supplied the space;
                                        once it fills the row the filter button lands flush against
                                        Add Listing. 0.75rem matches the gap used within each group. */}
                    {/* ERP-5233: replaced by the two boxes below. Kept commented so a
                        rollback is one edit. Pricing History's own SearchBox stays.

                    <SearchBox
                      searchInput={searchInput}
                      setSearchInput={setSearchInput}
                      setSearchTerm={setSearchTerm}
                      setCurrentPage={setCurrentPage}
                      disabled={isInitiatePricingToolbarEmpty}
                    />
                    */}
                    <ListingSearchBars
                      modelValue={modelSearchInput}
                      onModelValueChange={setModelSearchInput}
                      onModelCommit={(v, exact) => { setModelSearch({ term: v, exact: !!exact }); setCurrentPage(1); }}
                      detailValue={detailSearchInput}
                      onDetailValueChange={setDetailSearchInput}
                      onDetailCommit={(v) => { setDetailSearch(v); setCurrentPage(1); }}
                      disabled={isInitiatePricingToolbarEmpty}
                    />
                    <div style={{ position: "relative" }}>
                      <button
                        ref={filterButtonRef}
                        className={`icon-btn p-2 filter-btn ${filtersApplied ? "applied" : ""}`}
                        onClick={() => !isInitiatePricingToolbarEmpty && setFilterPopupOpen((o) => !o)}
                        disabled={isInitiatePricingToolbarEmpty}
                        style={{
                          border: isInitiatePricingToolbarEmpty
                            ? "1px solid var(--Gray-400, #CED4DA)"
                            : filtersApplied ? "1px solid #2C7BE5" : "1px solid #6C757D",
                          color: isInitiatePricingToolbarEmpty ? "#CED4DA" : filtersApplied ? "#2C7BE5" : "#6C757D",
                          background: isInitiatePricingToolbarEmpty
                            ? "var(--Gray-100, #F8F9FA)"
                            : filtersApplied ? "#CFE2FF" : "#FFFFFF",
                          height: 40,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          borderRadius: 8,
                          cursor: isInitiatePricingToolbarEmpty ? "not-allowed" : "pointer",
                          pointerEvents: isInitiatePricingToolbarEmpty ? "none" : "auto",
                        }}
                      >
                        {isInitiatePricingToolbarEmpty ? <FilterDisabledIcon /> : filtersApplied ? <FilterActiveIcon /> : <FilterInactiveIcon />}
                      </button>
                      <FilterDrawer
                        isOpen={filterPopupOpen}
                        onClose={() => { setTempValues({ status: [], platforms: [], setTypes: [] }); setFilterPopupOpen(false); }}
                        sections={filterSections}
                        tempValues={tempValues}
                        setTempValues={setTempValues}
                        onApply={() => {
                          setFilters({ status: tempValues.status || [], platforms: tempValues.platforms || [], setTypes: tempValues.setTypes || [] });
                          setFilterPopupOpen(false);
                        }}
                        onClear={() => {
                          const empty = { status: [], platforms: [], setTypes: [] };
                          setTempValues(empty);
                          setFilters(empty);
                        }}
                        enableAutoSwap={true}
                      />
                    </div>
                  </div>

                  {/* Bulk Pricing Button + clear-all. Explicit row here rather than
                      relying on .toolbar-right, whose flex rule is nested under
                      another page's selector in company.scss and may not reach
                      this page; with a single child before this it was never
                      exercised with two. */}
                  <div className="toolbar-right" style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    {/* Three states. The loading state follows the stakeholder
                        design (2026-09-18); the other two match Bulk Pricing's own
                        Save button:
                          nothing selected -> grey box, pencil, not-allowed
                          loading          -> the same grey box, a spinner in the
                                              pencil's place, the count badge kept,
                                              progress cursor
                          ready            -> black action button
                        The label never changes. The box itself (40px tall,
                        min-width 100, radius 8, shadow) is shared by all three so
                        nothing shifts as the state flips. Icon and spinner use
                        currentColor, so they take the box's text colour instead
                        of a white stroke vanishing on grey. */}
                    <button
                      type="button"
                      disabled={selectedIds.length === 0 || loadingAll}
                      onClick={selectedIds.length > 0 && !loadingAll ? handleStartBulkPricing : undefined}
                      style={{
                        position: "relative",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
                        height: 40, minWidth: 100, padding: "10px", borderRadius: 8,
                        boxShadow: "0 1px 4px 0 rgba(0,0,0,0.10)",
                        ...(selectedIds.length === 0 || loadingAll
                          ? { border: "1px solid #CED4DA", backgroundColor: "#E9ECEF", color: "#6C757D" }
                          : { border: "1px solid #212529", backgroundColor: "#212529", color: "#fff" }),
                        cursor: loadingAll ? "progress" : selectedIds.length === 0 ? "not-allowed" : "pointer",
                      }}
                    >
                      {loadingAll ? (
                        <Spinner size={18} color="currentColor" />
                      ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
  <path d="M9 2.25017H3.75C3.35218 2.25017 2.97064 2.40821 2.68934 2.68951C2.40804 2.97081 2.25 3.35235 2.25 3.75017V14.2502C2.25 14.648 2.40804 15.0295 2.68934 15.3108C2.97064 15.5921 3.35218 15.7502 3.75 15.7502H14.25C14.6478 15.7502 15.0294 15.5921 15.3107 15.3108C15.592 15.0295 15.75 14.648 15.75 14.2502V9.00017M13.7812 1.96892C14.0796 1.67055 14.4843 1.50293 14.9062 1.50293C15.3282 1.50293 15.7329 1.67055 16.0312 1.96892C16.3296 2.26729 16.4972 2.67196 16.4972 3.09392C16.4972 3.51588 16.3296 3.92055 16.0312 4.21892L9.2715 10.9794C9.09341 11.1574 8.8734 11.2876 8.63175 11.3582L6.477 11.9882C6.41246 12.007 6.34405 12.0081 6.27893 11.9914C6.21381 11.9748 6.15437 11.9409 6.10683 11.8933C6.0593 11.8458 6.02542 11.7864 6.00873 11.7212C5.99205 11.6561 5.99318 11.5877 6.012 11.5232L6.642 9.36842C6.71289 9.12696 6.8434 8.90721 7.0215 8.72942L13.7812 1.96892Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
                      )}
                      <span style={{ fontSize: 12, fontWeight: 500 }}>Bulk Pricing</span>
                      {selectedIds.length > 0 && (
                        <span style={{ minWidth: 16, height: 16, padding: "2px 5px", borderRadius: 999, backgroundColor: "#FFEB00", color: "#212529", fontSize: 11, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          {selectedIds.length}
                        </span>
                      )}
                    </button>

                    {/* Clear-all. Unlike the header checkbox, which only unticks
                        the CURRENT filter's result set, this drops the entire
                        selection in one go - every platform, every filter the
                        user built it up across. clearSelection is setSelectedIds([]),
                        so it needs no fetch and is instant. Enabled only while
                        something is selected. */}
                    <button
                      type="button"
                      onClick={clearSelection}
                      disabled={selectedIds.length === 0}
                      title="Clear all selected records across every search and filter"
                      aria-label="Clear all selected records"
                      style={{
                        display: "flex", alignItems: "center", justifyContent: "center",
                        height: 40, width: 40, padding: 0,
                        background: "none", border: "none",
                        color: selectedIds.length === 0 ? "#CED4DA" : "#6C757D",
                        cursor: selectedIds.length === 0 ? "not-allowed" : "pointer",
                      }}
                    >
                      <PaintbrushVertical size={20} style={{ transform: "rotate(180deg)" }} />
                    </button>
                  </div>
                </div>

                {/* Table */}
                <div
                  className="table-wrapper"
                  style={{
                    border: "none",
                    opacity: tableLoading ? 0.5 : 1,
                    pointerEvents: tableLoading ? "none" : undefined,
                    transition: "opacity .15s ease",
                  }}
                  ref={ppv2ScrollRef}
                  onScroll={handlePpv2Scroll}
                >
                  {isTableEmpty && !anySearchActive && !filtersApplied ? (
                    <div className="empty-state">
                      <div className="empty-state-content">
                        <img src={noBrandImage} alt="empty" style={{ width: 120, height: 120 }} />
                        <h1 className="no-item-title mt-2">No Listings Found for Pricing</h1>
                        <p className="no-item-text">Add a Listing first to initiate Pricing</p>
                      </div>
                    </div>
                  ) : isTableEmpty && (anySearchActive || filtersApplied) ? (
                    <div className="empty-state">
                      <div className="empty-state-content">
                        <img src={emptySearchImg} alt="no results" style={{ width: 120, height: 120 }} />
                        <h1 className="no-item-title mt-2">No Listings Found</h1>
                        <p className="no-item-text">No results match what you're looking for</p>
                      </div>
                    </div>
                  ) : (
                    <table className={`entities-table ppv2-table${ppv2ScrolledLeft ? " is-scrolled-left" : ""}${ppv2ScrolledRight ? " is-scrolled-right" : ""}`} style={{ borderCollapse: "separate", borderSpacing: 0, tableLayout: "fixed", width: "100%" }}>
                      <colgroup>
                        <col style={{ width: colWidths.stock }} />
                        <col style={{ width: colWidths.costPrice }} />
                        <col style={{ width: colWidths.platforms }} />
                        <col style={{ width: colWidths.basePrice }} />
                        <col style={{ width: colWidths.platformPrice }} />
                        <col style={{ width: colWidths.notes }} />
                        {/* ERP-5173: sticky Expand/Collapse column — same 84px as Listing's
                            Action column; also carries the card's right border. */}
                        <col style={{ width: 84 }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th
                            className="ppv2-sticky-edge"
                            style={{
                              position: "sticky",
                              left: 0,
                              zIndex: 5,
                              background: "#E9ECEF",
                              overflow: "visible",
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <div className="form-check" style={{ marginBottom: 0 }}>
                                <input
                                  className="form-check-input" type="checkbox"
                                  checked={allSelected} onChange={toggleAll}
                                  disabled={loadingAll}
                                  ref={(el) => { if (el) el.indeterminate = headerIndeterminate; }}
                                  title={
                                    fullMappingIds
                                      ? `Select all ${fullMappingIds.length} across every page`
                                      : `Select all — ${allMappingIds.length} on this page, more on the others`
                                  }
                                />
                              </div>
                              <span>Stock</span>
                              {/* Only the in-flight state is shown — the idle counts
                                  (this page's number, and the full total once fetched)
                                  added noise to the header without telling the user
                                  anything the checkbox itself does not. */}
                              {loadingAll && (
                                <span style={{ fontWeight: 400, fontSize: 11, color: "#6C757D", whiteSpace: "nowrap" }}>
                                  loading all…
                                </span>
                              )}
                            </div>
                            <ColResizeHandle colKey="stock" minWidth={150} />
                          </th>
                          <th style={{ position: "relative", overflow: "hidden" }}>
                            Cost Price
                            <ColResizeHandle colKey="costPrice" />
                          </th>
                          <th style={{ position: "relative", overflow: "hidden" }}>
                            Platforms
                            <ColResizeHandle colKey="platforms" />
                          </th>
                          <th style={{ position: "relative", overflow: "hidden" }}>
                            Base Price
                            <ColResizeHandle colKey="basePrice" />
                          </th>
                          <th style={{ position: "relative", overflow: "hidden" }}>
                            Platform Price
                            <ColResizeHandle colKey="platformPrice" />
                          </th>
                          <th style={{ position: "relative", overflow: "hidden" }}>
                            Notes
                            <ColResizeHandle colKey="notes" />
                          </th>
                          <th className="ppv2-right-edge" aria-hidden="true" style={{ position: "sticky", right: 0, zIndex: 5, overflow: "visible" }} />
                        </tr>
                      </thead>
                      <tbody>
                        {/* 10px between the header and the first card — the design
                            spaces them exactly like two cards, not flush. */}
                        <tr className="ppv2-group-gap" aria-hidden="true">
                          <td colSpan={7} />
                        </tr>
                        {grouped.map((group) => {
                          const isExpanded = !!expandedStocks[group.key];
                          const allPlatforms = group.listings.flatMap((l) =>
                            l.platforms.map((p) => ({ ...p, _listingId: l.id, _basePrice: l.basePrice }))
                          );
                          // ERP-5171 — see PLACEHOLDER_PARENT_ROW_LIMIT. Only the parent row is
                          // capped: the count row and the sub-rows still cover every mapping.
                          const rowPlatforms = isPlaceholderStock(group.name, group.variant)
                            ? allPlatforms.slice(0, PLACEHOLDER_PARENT_ROW_LIMIT)
                            : allPlatforms;
                          const hasHiddenPlatforms = rowPlatforms.length < allPlatforms.length;
                          const groupMappingIds = group.listings.flatMap((l) =>
                            l.platforms.filter((p) => p.syncStatus !== "FAILED").map((p) => p.sales_listing_mapping_id)
                          );
                          const groupAllSelected = groupMappingIds.length > 0 && groupMappingIds.every((id) => selectedSet.has(id));
                          const groupSomeSelected = groupMappingIds.some((id) => selectedSet.has(id));
                          // Disabled only when no listing in the group is selectable (all FAILED)
                          const groupDisabled = groupMappingIds.length === 0;

                          return (
                            <React.Fragment key={group.key}>
                              {/* Stock parent row */}
                              <tr className="ppv2-stock-row">
                                {/* Stock info — the checkbox lives in THIS cell, not a column of its
                                    own. A separate 40px column could not give a child row a visible
                                    indent: whatever the checkbox was shifted past its own cell got
                                    painted over by the next sticky cell regardless of z-index, and
                                    widening the column left a big gap because table-layout:fixed
                                    redistributes the slack proportionally. Inside one cell the
                                    child's padding-left moves the checkbox and the content together
                                    — which is exactly what the reference design does. */}
                                <td className="ppv2-sticky-edge" style={{ position: "sticky", left: 0, zIndex: 4, background: "#fff", overflow: "hidden" }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                                    <div className="form-check" onClick={(e) => e.stopPropagation()} style={{ marginBottom: 0, flexShrink: 0 }}>
                                    <input
                                      className="form-check-input" type="checkbox"
                                      checked={groupAllSelected}
                                      disabled={groupDisabled}
                                      style={groupDisabled ? {
                                        cursor: "not-allowed",
                                        borderRadius: 4,
                                        border: "1px solid #ADB5BD",
                                        background: "#E9ECEF",
                                        boxShadow: "0 1px 4px 0 rgba(0,0,0,0.10)",
                                        appearance: "none",
                                        WebkitAppearance: "none",
                                        width: 20,
                                        height: 20,
                                      } : undefined}
                                      ref={(el) => {
                                        if (el) el.indeterminate = groupSomeSelected && !groupAllSelected;
                                      }}
                                      onChange={() => {
                                        if (groupDisabled) return;
                                        if (groupAllSelected) {
                                          setSelectedIds((prev) => prev.filter((id) => !groupMappingIds.includes(id)));
                                        } else {
                                          setSelectedIds((prev) => [...new Set([...prev, ...groupMappingIds])]);
                                        }
                                      }}
                                    />
                                    </div>
                                    <img src={group.avatar} alt="avatar" style={{ width: 32, height: 32, objectFit: "cover", borderRadius: 4, flexShrink: 0 }} />
                                    <div style={{ minWidth: 0, overflow: "hidden" }}>
                                      <div style={{ fontWeight: 400, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{group.name}</div>
                                      <div className="sub-line mt-1" style={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{group.variant}{group.model_number ? ` | ${group.model_number}` : ""}</div>
                                      <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
                                        <StockTags condition={group.listings[0]?.condition} conditionType={group.listings[0]?.grading} setType={group.setType} style={{ minWidth: "unset", padding: "2px 5px", fontSize: 8, lineHeight: "normal", background: "#FFEB00" }} />
                                      </div>
                                    </div>
                                  </div>
                                </td>

                                {/* Cost Price */}
                                <td style={{ fontSize: 12, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                                  {group.costPriceData
                                    ? group.costPriceData.is_uniform
                                      ? `SGD ${formatPrice(group.costPriceData.min)}`
                                      : <span>SGD {formatPrice(group.costPriceData.min)}<span style={{ margin: "0 4px", color: "#6C757D" }}>→</span>SGD {formatPrice(group.costPriceData.max)}</span>
                                    : <span style={{ color: "#ADB5BD" }}>-</span>}
                                </td>

                                {/* Platforms */}
                                <td style={{ overflow: "hidden" }}>
                                  <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
                                    {rowPlatforms.map((p, i) => (
                                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: p.textColor, minWidth: 0 }}>
                                        <svg width="8" height="8" viewBox="0 0 8 8" fill="none" style={{ flexShrink: 0 }}>
                                          <circle cx="4" cy="4" r="3" fill={p.dotColor} />
                                        </svg>
                                        <span style={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{p.name}</span>
                                        {p.isDelisted && (
                                          <span style={DELIST_TAG_STYLE}>Delist</span>
                                        )}
                                        {p.isCampaign && (
                                          <span title={CAMPAIGN_TAG_TITLE} style={CAMPAIGN_TAG_STYLE}>Campaign</span>
                                        )}
                                      </div>
                                    ))}
                                    {hasHiddenPlatforms && <MoreMappingsEllipsis />}
                                  </div>
                                </td>

                                {/* Base Price — edit at stock level, applies to all platform listings.
                                    ERP-5169: the pencil is disabled when any listing in the group has a
                                    priced WooCommerce mapping, because the backend re-derives base_price
                                    from that Woo price after every write — a typed value would come back
                                    changed with no explanation. "Any", not "every": this one edit writes
                                    the same figure to every listing in the group, so a group holding even
                                    one Woo-driven listing cannot be edited here without part of the write
                                    being silently reverted. When nothing is Woo-driven AND no listing in
                                    the group has a base price yet, the pencil breathes instead. */}
                                <td style={{ overflow: "hidden" }}>
                                  {(() => {
                                    const firstWithPrice = group.listings.find(l => !isBasePriceEmpty(l.basePrice));
                                    const wooDriven = group.listings.some(l => isWooDrivenBasePrice(l));
                                    const needsBasePrice = !wooDriven && !firstWithPrice;
                                    // minHeight 24: the dot is 10px inside a halo that swells to
                                    // 7px, so it needs 24px of height, but this wrapper is only as
                                    // tall as the 18px pencil button and company.scss clips every
                                    // `td > div` (overflow:hidden for text truncation) — the halo
                                    // was cut flat top and bottom (Jess, 16 Sep). Reserving the
                                    // height keeps the halo whole without unclipping the cell.
                                    return (
                                      <div style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0, minHeight: 24 }}>
                                        <span style={{ fontSize: 12, color: "#212529", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                                          {firstWithPrice ? formatSGD(firstWithPrice.basePrice) : <span style={{ color: "#ADB5BD" }}>-</span>}
                                        </span>
                                        <button
                                          className="ppv2-edit-btn"
                                          title={wooDriven ? WOO_BASE_PRICE_LOCKED_TITLE : needsBasePrice ? BASE_PRICE_BREATHE_TITLE : "Edit base price"}
                                          disabled={wooDriven}
                                          onClick={(e) => {
                                            if (wooDriven) return;
                                            startEdit(e, group.listings[0]?.id, "base_price_group", null, null, firstWithPrice?.basePrice ?? null, group.listings);
                                          }}
                                          style={{ flexShrink: 0 }}
                                        >
                                          <EditIcon />
                                        </button>
                                        {needsBasePrice && (
                                          <span className="ppv2-breathe-dot" title={BASE_PRICE_BREATHE_TITLE} />
                                        )}
                                      </div>
                                    );
                                  })()}
                                </td>

                                {/* Platform Price — read-only in parent row */}
                                <td style={{ overflow: "hidden" }}>
                                  <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
                                    {rowPlatforms.map((p, i) => (
                                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0 }}>
                                        <span style={{ fontSize: 12, color: p.textColor, whiteSpace: "nowrap" }}>
                                          {renderErpPriceValue(p)}
                                        </span>
                                      </div>
                                    ))}
                                    {hasHiddenPlatforms && <MoreMappingsEllipsis />}
                                  </div>
                                </td>

                                {/* Notes — stock-level remark, read-only */}
                                <td style={{ overflow: "hidden" }}>
                                  <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
                                    {(() => {
                                      const noted = group.listings.filter((l) => (l.notes || "").trim());
                                      if (!noted.length) return <span style={{ color: "#ADB5BD" }}>-</span>;
                                      return noted.map((l) => (
                                        <div key={l.id} title={l.notes} style={{ fontSize: 12, color: "#212529", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                                          {l.notes}
                                        </div>
                                      ));
                                    })()}
                                  </div>
                                </td>
                                <td className="ppv2-right-edge" aria-hidden="true" style={{ position: "sticky", right: 0, zIndex: 4, background: "#fff" }} />
                              </tr>

                              {/* Count row — split into per-column cells (rather than one
                                  colSpan={5} cell) so the listing-count label can sit in the
                                  same sticky "Stock" column and stay fixed while scrolling,
                                  same as the row above it. */}
                              <tr className={`ppv2-count-row${isExpanded ? "" : " ppv2-group-bottom"}`} style={{ cursor: "pointer" }} onClick={() => toggleExpand(group.key)}>
                                <td className="ppv2-sticky-edge" style={{ padding: "10px", position: "sticky", left: 0, zIndex: 4, background: "#F8F9FA", verticalAlign: "middle" }}>
                                  <span className="ppv2-listing-count" style={{fontWeight:600}}>
                                    {allPlatforms.length} {allPlatforms.length === 1 ? "Listing" : "Listings"}
                                  </span>
                                </td>
                                <td colSpan={5} style={{ verticalAlign: "middle" }} />
                                {/* ERP-5173: Expand/Collapse lives in the sticky right column, so it
                                    and the card's right border stay put while the middle scrolls. */}
                                <td className="ppv2-right-edge" style={{ textAlign: "right", padding: "14px 6px 14px 4px", whiteSpace: "nowrap", position: "sticky", right: 0, zIndex: 4, background: "#F8F9FA" }}>
                                  <span className="ppv2-expand-btn" style={{ justifyContent: "flex-end", display: "inline-flex", alignItems: "center", gap: 4 }}>
                                    {isExpanded ? <><span>Collapse</span><ChevronUp size={14} /></> : <><span>Expand</span><ChevronDown size={14} /></>}
                                  </span>
                                </td>
                              </tr>

                              {/* Sub-rows (expanded) */}
                              {isExpanded && allPlatforms.map((p, pi) => {
                                const isLast = pi === allPlatforms.length - 1;
                                // Find the listing for this platform
                                const parentListing = group.listings.find((l) => l.id === p._listingId);
                                return (
                                  <tr key={`sub-${p._listingId}-${pi}`} className={isLast ? "ppv2-subrow ppv2-subrow-last ppv2-group-bottom" : "ppv2-subrow"}>
                                                                        {/* Col 1 — elbow + Product Name + Product ID. The 32px
                                        indent lives in .ppv2-subrow td:nth-child(2) so the
                                        elbow lines up with the padding edge, not the text. */}
                                    <td className="ppv2-sticky-edge" style={{ fontWeight: 400, position: "sticky", left: 0, zIndex: 4, background: "#F8F9FA", overflow: "hidden" }}>
                                      <div style={{ display: "flex", alignItems: "flex-start", gap: 7, minWidth: 0 }}>
                                        <div className="form-check" onClick={(e) => e.stopPropagation()} style={{ marginBottom: 0, flexShrink: 0 }}>
                                          <input
                                            className="form-check-input" type="checkbox"
                                            checked={selectedSet.has(p.sales_listing_mapping_id)}
                                            disabled={p.syncStatus === "FAILED"}
                                            style={p.syncStatus === "FAILED" ? {
                                              cursor: "not-allowed",
                                              borderRadius: 4,
                                              border: "1px solid #ADB5BD",
                                              background: "#E9ECEF",
                                              boxShadow: "0 1px 4px 0 rgba(0,0,0,0.10)",
                                              appearance: "none",
                                              WebkitAppearance: "none",
                                              width: 20,
                                              height: 20,
                                            } : { width: 20, height: 20 }}
                                            onChange={() => {
                                              if (p.syncStatus === "FAILED") return;
                                              const mid = p.sales_listing_mapping_id;
                                              setSelectedIds((prev) =>
                                                prev.includes(mid) ? prev.filter((x) => x !== mid) : [...prev, mid]
                                              );
                                            }}
                                          />
                                        </div>
                                        <span className="ppv2-subrow-elbow" aria-hidden="true">└</span>
                                        <div style={{ minWidth: 0, flex: 1 }}>
                                          {p.platformProductName
                                            ? <div style={{ color: p.textColor, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", fontSize: 12 }}>{p.platformProductName}</div>
                                            : <span style={{ color: "#6C757D" }}>-</span>}
                                          {p.platformProductId && (
                                            <div style={{ fontSize: 10, color: "#6C757D", marginTop: 2, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                                              Product ID: {p.platformProductId}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </td>

                                    {/* Col 2 — SKU Name + SKU ID */}
                                    <td style={{ alignContent: 'flex-start', overflow: "hidden" }}>
                                      {p.platformSkuName
                                        ? <div style={{ color: p.textColor, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", fontSize: 12 }}>{p.platformSkuName}</div>
                                        : <span style={{ color: "#6C757D" }}>-</span>}
                                      {p.platformSkuId && (
                                        <div style={{ fontSize: 10, color: "#6C757D", marginTop: 2, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                                          SKU ID: {p.platformSkuId}
                                        </div>
                                      )}
                                    </td>

                                    {/* Platform dot + name */}
                                    <td style={{ alignContent: 'flex-start'}}>
                                      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: p.textColor }}>
                                        <svg width="8" height="8" viewBox="0 0 8 8" fill="none" style={{ flexShrink: 0 }}>
                                          <circle cx="4" cy="4" r="3" fill={p.dotColor} />
                                        </svg>
                                        <span style={{ flexShrink: 0 }}>{p.name}</span>
                                        {p.isDelisted && (
                                          <span style={DELIST_TAG_STYLE}>Delist</span>
                                        )}
                                        {p.isCampaign && (
                                          <span title={CAMPAIGN_TAG_TITLE} style={CAMPAIGN_TAG_STYLE}>Campaign</span>
                                        )}
                                      </div>
                                    </td>

                                    {/* Base Price — read-only in sub-row, edit at stock level above */}
                                    <td style={{ fontSize: 12, color: "#212529", alignContent: 'flex-start' }}>
                                      {p._basePrice != null && p._basePrice !== 0 ? formatSGD(p._basePrice) : <span style={{ color: "#ADB5BD" }}>-</span>}
                                    </td>

                                    {/* Platform price (override_price) — edit here per platform */}
                                    <td style={{ fontSize: 12, color: p.textColor, alignContent: 'flex-start' }}>
                                      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                          <span>{p.erpPrice !== "-" ? p.erpPrice : <span style={{ color: "#ADB5BD" }}>-</span>}</span>
                                          <button
                                            className="ppv2-edit-btn"
                                            title={p.syncStatus === "FAILED" ? "Cannot edit — sync failed" : "Edit platform price"}
                                            disabled={p.syncStatus === "FAILED"}
                                            style={p.syncStatus === "FAILED" ? { opacity: 0.4, cursor: "not-allowed" } : {}}
                                            onClick={(e) => {
                                              if (p.syncStatus === "FAILED") return;
                                              startEdit(e, p._listingId, "platform_price", p.sales_listing_mapping_id, p.id, p.override_price_raw);
                                            }}
                                          >
                                            <EditIcon />
                                          </button>
                                        </div>
                                        {shouldShowNormalPrice(p) && (
                                          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                            <span style={{ textDecoration: "line-through", color: "#666666" }}>{p.normalPrice}</span>
                                            <NormalPriceInfo />
                                          </div>
                                        )}
                                      </div>
                                    </td>

                                    {/* Notes — listing-level remark, read-only */}
                                    <td title={p.notes || ""} style={{ fontSize: 12, color: "#212529", alignContent: 'flex-start', overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                                      {(p.notes || "").trim() || <span style={{ color: "#ADB5BD" }}>-</span>}
                                    </td>
                                    <td className="ppv2-right-edge" aria-hidden="true" style={{ position: "sticky", right: 0, zIndex: 4, background: "#F8F9FA" }} />
                                  </tr>
                                );
                              })}

                              {/* Gap between cards. A spacer row rather than a margin:
                                  table rows cannot take one, and border-spacing would
                                  open a gap between every cell, not just between groups. */}
                              <tr className="ppv2-group-gap" aria-hidden="true">
                                <td colSpan={7} />
                              </tr>
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  )}

                  {showDuplicateWarning && (
                    <div className="d-flex align-items-center justify-content-between" role="alert" style={{ boxShadow: "0px 4px 10px 0px #0000001F", padding: "15px 10px", borderRadius: 12, border: "1px solid #FFC107", backgroundColor: "#FFF3CD", color: "#664D03", fontSize: 12, position: "fixed", top: 10, width: "calc(100% - 20px)", maxWidth: 610, left: "50%", transform: "translateX(-50%)", zIndex: 2000 }}>
                      <div className="d-flex align-items-center">
                        <Info className="me-2" />
                        <strong>Duplicate entries weren't added</strong> — All other items have been successfully added
                      </div>
                      <X size={16} style={{ cursor: "pointer" }} onClick={() => setShowDuplicateWarning(false)} />
                    </div>
                  )}
                </div>

                {/* Pagination */}
                {!isTableEmpty && (
                  <div className="pagination" style={{ marginTop: 16 }}>
                    <div className="pagination-info">
                      {`${(currentPage - 1) * PAGE_SIZE + 1} - ${Math.min(currentPage * PAGE_SIZE, totalCount)} of ${totalCount}`}
                    </div>
                    <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>
        )} {/* end activeTab === "initiate-pricing" */}

        {activeTab === "pricing-history" && (
          <div className="tab-content" id="price-history-content">
            <div className="info-section">
              <div className="info-card">
                <div className="entities-tab">
                  <div className="toolbar">
                    <div className="toolbar-left">
                      <SearchBox
                        searchInput={phSearchInput}
                        setSearchInput={setPhSearchInput}
                        setSearchTerm={setPhSearchTerm}
                        setCurrentPage={setPhCurrentPage}
                        disabled={phIsInitialEmpty}
                      />
                      <button
                        className={`icon-btn p-2 filter-btn ${phAppliedFilter ? "applied" : ""}`}
                        onClick={() => !phIsInitialEmpty && setPhFilterPopupOpen((prev) => !prev)}
                        disabled={phIsInitialEmpty}
                        style={{
                          border: phIsInitialEmpty
                            ? "1px solid var(--Gray-400, #CED4DA)"
                            : phAppliedFilter ? "1px solid #2C7BE5" : "1px solid #6C757D",
                          color: phIsInitialEmpty ? "#CED4DA" : phAppliedFilter ? "#2C7BE5" : "#6C757D",
                          background: phIsInitialEmpty
                            ? "var(--Gray-100, #F8F9FA)"
                            : phAppliedFilter ? "#CFE2FF" : "#FFFFFF",
                          height: 40, display: "flex", alignItems: "center", justifyContent: "center",
                          borderRadius: 8,
                          cursor: phIsInitialEmpty ? "not-allowed" : "pointer",
                          pointerEvents: phIsInitialEmpty ? "none" : "auto",
                        }}
                      >
                        {phIsInitialEmpty ? <FilterDisabledIcon /> : phAppliedFilter ? <FilterActiveIcon /> : <FilterInactiveIcon />}
                      </button>
                      <FilterDrawer
                        isOpen={phFilterPopupOpen}
                        onClose={() => setPhFilterPopupOpen(false)}
                        sections={phSections}
                        tempValues={phTempValues}
                        setTempValues={setPhTempValues}
                        onApply={() => {
                          const hasAny = phTempValues.changesTime_start || phTempValues.changesTime_end
                            || phTempValues.initiator?.length > 0
                            || phTempValues.pricingObject?.length > 0
                            || phTempValues.platform?.length > 0
                            || phTempValues.pricingStatus?.length > 0;
                          setPhAppliedFilter(hasAny ? {
                            changesTime_start: phTempValues.changesTime_start,
                            changesTime_end: phTempValues.changesTime_end,
                            initiator: phTempValues.initiator || [],
                            pricingObject: phTempValues.pricingObject || [],
                            platform: phTempValues.platform || [],
                            // NOTE: this object is a whitelist — a section added to
                            // phSections does nothing until its key is forwarded here.
                            pricingStatus: phTempValues.pricingStatus || [],
                          } : null);
                          setPhCurrentPage(1);
                          setPhFilterPopupOpen(false);
                        }}
                        onClear={() => {
                          setPhTempValues({ changesTime_start: null, changesTime_end: null, initiator: [], pricingObject: [], platform: [], pricingStatus: [] });
                          setPhAppliedFilter(null);
                        }}
                      />
                    </div>
                  </div>

                  <div className="table-wrapper" style={flattenedPriceHistory.length === 0 ? { borderBottom: "none" } : undefined}>
                    {phIsInitialEmpty ? (
                      <div className="empty-state">
                        <div className="empty-state-content">
                          <img src={noBrandImage} alt="empty" style={{ width: 120, height: 120 }} />
                          <h1 className="no-item-title mt-2">No Pricing History</h1>
                          <p className="no-item-text">Price changes will appear here once pricing is initiated</p>
                        </div>
                      </div>
                    ) : flattenedPriceHistory.length === 0 ? (
                      <div className="empty-state">
                        <div className="empty-state-content">
                          <img src={emptySearchImg} alt="no results" style={{ width: 120, height: 120 }} />
                          <h1 className="no-item-title mt-2">No History Found</h1>
                          <p className="no-item-text">No results match what you're looking for</p>
                        </div>
                      </div>
                    ) : (
                      <table className="entities-table ppv2-ph-table" style={{ borderCollapse: "separate", borderSpacing: 0, tableLayout: "fixed", width: "100%" }}>
                        <thead>
                          <tr>
                            <SortableHeader
                              label="Changes Time" field="changeTime" sortField={phSortField} sortOrder={phSortOrder} handleSort={handlePhSort}
                              style={{ height: 45, padding: "10px", verticalAlign: "middle", whiteSpace: "nowrap", position: "relative", overflow: "hidden", width: phColWidths.changeTime }}
                              resizeHandle={<PhColResizeHandle colKey="changeTime" minWidth={110} />}
                            />
                            <SortableHeader
                              label="Initiator" field="initiator" sortField={phSortField} sortOrder={phSortOrder} handleSort={handlePhSort}
                              style={{ height: 45, padding: "10px", verticalAlign: "middle", whiteSpace: "nowrap", position: "relative", overflow: "hidden", width: phColWidths.initiator }}
                              resizeHandle={<PhColResizeHandle colKey="initiator" minWidth={90} />}
                            />
                            <SortableHeader
                              label="Stock" field="stock" sortField={phSortField} sortOrder={phSortOrder} handleSort={handlePhSort}
                              style={{ height: 45, padding: "10px", verticalAlign: "middle", whiteSpace: "nowrap", position: "relative", overflow: "hidden", width: phColWidths.stock }}
                              resizeHandle={<PhColResizeHandle colKey="stock" minWidth={150} />}
                            />
                            <th style={{ height: 45, padding: "10px", verticalAlign: "middle", fontFamily: "Montserrat", fontSize: 12, fontWeight: 700, color: "#212529", position: "relative", overflow: "hidden", width: phColWidths.basePriceChanges }}>
                              Base Price Changes
                              <PhColResizeHandle colKey="basePriceChanges" minWidth={140} />
                            </th>
                            <SortableHeader
                              label="Platform Listing" field="platform" sortField={phSortField} sortOrder={phSortOrder} handleSort={handlePhSort}
                              style={{ height: 45, padding: "10px", verticalAlign: "middle", whiteSpace: "nowrap", position: "relative", overflow: "hidden", width: phColWidths.platform }}
                              resizeHandle={<PhColResizeHandle colKey="platform" minWidth={100} />}
                            />
                            <th style={{ height: 45, padding: "10px", verticalAlign: "middle", fontFamily: "Montserrat", fontSize: 12, fontWeight: 700, color: "#212529", position: "relative", overflow: "hidden", width: phColWidths.platformPriceChanges }}>
                              Platform Price Changes
                              <PhColResizeHandle colKey="platformPriceChanges" minWidth={140} />
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {phPageData.map((row, idx) => {
                            const baseChange = getPriceChangeInfo(row.baseBefore, row.baseAfter);
                            const platChange = row.platform ? getPriceChangeInfo(row.platform.beforePrice, row.platform.afterPrice) : null;
                            const hasBaseChange = !!baseChange;
                            const hasPlatChange = !!platChange;
                            // Show each column only when that price actually changed
                            const showBase = hasBaseChange;
                            const showPlat = hasPlatChange;
                            // Success/error icon reflects the platform SYNC result, not the
                            // price direction. A change that synced shows the green success
                            // icon; a failed sync shows the red error icon. Base-price-only
                            // rows (no platform sync) are treated as success.
                            // ERP-5170: the row's status decides the icon, through the same
                            // bucketing the Pricing Status filter uses — so the icon and the
                            // filter can never disagree about what a row is. A campaign-locked
                            // push is Pending, not Failed, and the base price change beside it
                            // was saved either way: it never needed the platform's permission.
                            // phStatusKey only returns "pending" for Lazada now, so a pending
                            // row IS a campaign lock — no second test needed here.
                            const platKey = row.platform ? phStatusKey(row.platform.syncStatus, row.platform.name) : null;
                            const baseSynced = row.platform ? platKey !== "failed" : true;
                            const platIcon = !showPlat ? null
                              : platKey === "pending" ? <PlatformPendingIcon />
                              : platKey === "success_delayed" ? <PlatformDelayedIcon />
                              : platKey === "success" ? <PlatformMatchIcon />
                              : <PlatformFailedIcon />;
                            const upColor = "#13795B";
                            const downColor = "#B02A37";
                            return (
                              <tr key={`${row.historyId}-${row.id}-${idx}`}>
                                {/* Changes Time */}
                                <td style={{ fontSize: 12, color: "#212529", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                  {formatDateTime(row.time)}
                                </td>

                                {/* Initiator */}
                                <td style={{ fontSize: 12, color: "#212529", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                  {row.initiator?.brandName || "-"}
                                </td>

                                {/* Stock */}
                                <td style={{ overflow: "hidden" }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                                    <img src={row.avatar} alt="" style={{ width: 36, height: 36, borderRadius: 6, objectFit: "cover", flexShrink: 0 }} />
                                    <div style={{ minWidth: 0, overflow: "hidden" }}>
                                      <div style={{ fontWeight: 400, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{row.stockName}</div>
                                      <div className="sub-line mt-1" style={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{row.variant}{row.model_number ? ` | ${row.model_number}` : ""}</div>
                                      <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
                                        <StockTags condition={row.condition} conditionType={row.grading} setType={row.setType} style={{ minWidth: "unset", padding: "2px 5px", fontSize: 8, lineHeight: "normal", background: "#FFEB00" }} />
                                      </div>
                                    </div>
                                  </div>
                                </td>

                                {/* Base Price Changes */}
                                <td style={{ overflow: "hidden" }}>
                                  {showBase ? (
                                    <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                                      <div style={{ flexShrink: 0, marginTop: 1 }}>
                                        {baseSynced ? <PlatformMatchIcon /> : <PlatformFailedIcon />}
                                      </div>
                                      <div style={{ minWidth: 0, overflow: "hidden" }}>
                                        <div style={{ fontSize: 12, color: "#212529", fontFamily: "Montserrat", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                          {row.baseBefore === "BLANK" ? "-" : row.baseBefore === "-" ? "-" : row.baseBefore}
                                          <span style={{ margin: "0 4px", color: "#6C757D" }}>→</span>
                                          {row.baseAfter === "BLANK" ? "-" : row.baseAfter}
                                        </div>
                                        {!baseChange.isCleared && (
                                        <div style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>
                                          {baseChange.direction === "up" ? <PriceUpArrow /> : <PriceDownArrow />}
                                          <span style={{ fontSize: 12, fontWeight: 400, color: baseChange.direction === "up" ? upColor : downColor, fontFamily: "Montserrat" }}>
                                            SGD {baseChange.amount != null ? baseChange.amount.toFixed(2) : ""}
                                          </span>
                                        </div>
                                        )}
                                      </div>
                                    </div>
                                  ) : (
                                    <span style={{ color: "#ADB5BD", fontSize: 12 }}>-</span>
                                  )}
                                </td>

                                {/* Platform Listing — platform, then WHICH listing was repriced.
                                    The platform name alone was ambiguous whenever a stock carried
                                    more than one listing on the same platform. The three lines
                                    (chip / product / SKU) are 4px apart, set as a flex gap so all
                                    the spacings stay identical. */}
                                <td style={{ overflow: "hidden" }}>
                                  {showPlat && row.platform ? (
                                    <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 4 }}>
                                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                        <span className="ppv2-platform-chip">{row.platform.name}</span>
                                        {platKey === "pending" && (
                                          <span title={CAMPAIGN_TAG_TITLE} style={CAMPAIGN_TAG_STYLE}>Campaign</span>
                                        )}
                                      </div>
                                      <div title={[row.platform.productName, row.platform.productId && `(Product ID: ${row.platform.productId})`].filter(Boolean).join(" ")} style={{ fontSize: 12, lineHeight: "15px", color: "#212529", fontFamily: "Montserrat", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                                        {row.platform.productName || <span style={{ color: "#ADB5BD" }}>-</span>}
                                        {row.platform.productId && ` (Product ID: ${row.platform.productId})`}
                                      </div>
                                      <div title={[row.platform.skuName, row.platform.skuId && `(SKU ID: ${row.platform.skuId})`].filter(Boolean).join(" ")} style={{ fontSize: 10, lineHeight: "12px", color: "#6C757D", fontFamily: "Montserrat", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                                        {row.platform.skuName || "-"}
                                        {row.platform.skuId && ` (SKU ID: ${row.platform.skuId})`}
                                      </div>
                                    </div>
                                  ) : (
                                    <span style={{ color: "#ADB5BD", fontSize: 12 }}>-</span>
                                  )}
                                </td>

                                {/* Platform Price Changes */}
                                <td style={{ overflow: "hidden" }}>
                                  {showPlat ? (
                                    <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                                      {platIcon && (
                                        <div style={{ flexShrink: 0, marginTop: 1 }}>
                                          {platIcon}
                                        </div>
                                      )}
                                      <div style={{ minWidth: 0, overflow: "hidden" }}>
                                        <div style={{ fontSize: 12, color: "#212529", fontFamily: "Montserrat", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                          {row.platform.beforePrice === "-" ? "-" : row.platform.beforePrice}
                                          <span style={{ margin: "0 4px", color: "#6C757D" }}>→</span>
                                          {row.platform.afterPrice}
                                        </div>
                                        <div style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%" }}>
                                          {platChange.direction === "up" ? <PriceUpArrow /> : <PriceDownArrow />}
                                          <span style={{ fontSize: 12, fontWeight: 400, color: platChange.direction === "up" ? upColor : downColor, fontFamily: "Montserrat" }}>
                                            SGD {platChange.amount.toFixed(2)}
                                          </span>
                                        </div>
                                        {/* ERP-5170: the amber marker on its own only says "not
                                            the usual green". Spell out what actually happened, so
                                            history answers the question people opened it with —
                                            "did my price go out?" — without a hover. */}
                                        {platKey === "pending" && (
                                          <div style={{ fontSize: 10, lineHeight: "13px", color: "#997404", fontFamily: "Montserrat", marginTop: 2, whiteSpace: "normal" }}>
                                            Saved in ERP — pushes to the platform once the campaign ends
                                          </div>
                                        )}
                                        {platKey === "success_delayed" && row.platform.appliedAt && (
                                          <div style={{ fontSize: 10, lineHeight: "13px", color: "#6C757D", fontFamily: "Montserrat", marginTop: 2, whiteSpace: "nowrap" }}>
                                            Applied Time: {formatDateTime(row.platform.appliedAt)}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ) : (
                                    <span style={{ color: "#ADB5BD", fontSize: 12 }}>-</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {flattenedPriceHistory.length > 0 && (
                    <div className="pagination" style={{ marginTop: 16 }}>
                      <div className="pagination-info">
                        {`${(phCurrentPage - 1) * PH_PAGE_SIZE + 1} - ${Math.min(phCurrentPage * PH_PAGE_SIZE, flattenedPriceHistory.length)} of ${flattenedPriceHistory.length}`}
                      </div>
                      <Pagination currentPage={phCurrentPage} totalPages={phTotalPages} onPageChange={setPhCurrentPage} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )} {/* end activeTab === "pricing-history" */}

      </div>

      {/* Price edit popover — fixed position, appears above the edit button */}
      {editCell && editCell.anchorRect && (() => {
        const rect = editCell.anchorRect;
        // ERP-5169. True when saving THIS price is what the listing's Base Price will
        // follow. Not simply "is WooCommerce": a listing can carry more than one Woo
        // mapping and only the first priced one drives the base. Two cases qualify —
        // this mapping is already the driver, or nothing drives it yet (its Woo price
        // is 0 or unset), in which case this edit is what makes it the driver.
        const editWillDriveBasePrice = (() => {
          if (!editCell || editCell.field !== "platform_price") return false;
          const listing = listings.find((l) => l.id === editCell.listingId);
          if (!listing) return false;
          const platform = (listing.platforms || []).find(
            (p) => p.sales_listing_mapping_id === editCell.mappingId
          );
          if (!platform || !isWooPlatformName(platform.name)) return false;
          if (isPlaceholderStock(listing.name, listing.variant)) return false;
          const driver = wooBasePriceDriver(listing);
          return driver == null || driver.sales_listing_mapping_id === editCell.mappingId;
        })();

        const popoverWidth = 185;
        const popoverHeight = 45;
        const scrollX = window.scrollX || 0;
        const scrollY = window.scrollY || 0;
        // Center the arrow pointer directly above the edit button
        let left = rect.left + scrollX + rect.width / 2 - popoverWidth / 2;
        if (left < 8) left = 8;
        if (left + popoverWidth > window.innerWidth - 8) left = window.innerWidth - popoverWidth - 8;
        const top = rect.top + scrollY - popoverHeight - 10;
        return (
          <div
            className="ppv2-popover"
            style={{ left, top }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ppv2-popover-row">
              <span className={`ppv2-sgd-prefix${editError ? " error" : ""}`}>SGD</span>
              <input
                className={`ppv2-pop-input${editError ? " error" : ""}`}
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={editCell.value}
                autoFocus
                onChange={(e) => {
                  // ERP-5169 — a price is never negative. commitEdit already refuses
                  // newVal < 0, but the "-" could still be typed; stripping here also
                  // covers a pasted value, which fires no keydown.
                  const v = e.target.value.replace(/-/g, "");
                  setEditCell((prev) => ({ ...prev, value: v }));
                  if (editError) setEditError(false);
                }}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === "-") { e.preventDefault(); return; }
                  if (e.key === "Enter") commitEdit(e);
                  if (e.key === "Escape") cancelEdit(e);
                }}
                onBlur={(e) => {
                  const isBaseField = editCell.field === "base_price" || editCell.field === "base_price_group";
                  if (isBaseField) return;
                  const trimmed = e.target.value.trim();
                  if (trimmed === "") return;
                  const newVal = parseFloat(trimmed);
                  const err = getLazadaMaxPriceError(editCell.listingId, editCell.mappingId, newVal);
                  if (err) setEditError(err);
                }}
              />
              <div className="ppv2-popover-actions">
                <button className="ppv2-pop-save" onClick={commitEdit} title="Save">
                  <Check size={14} strokeWidth={2.5} />
                </button>
                <button className="ppv2-pop-cancel" onClick={cancelEdit} title="Cancel">
                  <X size={14} strokeWidth={2.5} />
                </button>
              </div>
            </div>
            {editError && editCell && editCell.field !== "base_price" && editCell.field !== "base_price_group" && (
              <div className="ppv2-error-msg" style={{ whiteSpace: "pre-line" }}>
                {typeof editError === "string" ? editError : "Required"}
              </div>
            )}
            {/* ERP-5169: editing a WooCommerce price silently moves the listing's
                Base Price too — the backend re-derives it from this figure. Say so
                here, where the decision is being made, rather than leaving the user
                to notice afterwards that a number they did not touch had changed. */}
            {editWillDriveBasePrice && (
              <div className="ppv2-pop-hint">Base Price will match this price</div>
            )}
          </div>
        );
      })()}

    </div>
  );
}
